import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaArrowRight, FaSync, FaSearch, FaTrash,
  FaCheck, FaTimes, FaArrowUp, FaArrowDown,
  FaUndo, FaRedo, FaSearchPlus, FaSearchMinus, FaCompress,
  FaMapMarkerAlt, FaCamera, FaFolder, FaFolderOpen, FaFolderPlus, FaLayerGroup, FaCopy, FaBolt, FaInbox
} from "react-icons/fa";
import { API_BASE } from "../api/config";
import { cleanFilenameForTrello, buildTrelloTitle, extractGrundKeywords } from "../utils/trelloUtils";
import useVerschiebenJobs from "../utils/useVerschiebenJobs";
import VerschiebenJobIndicator from "../components/VerschiebenJobIndicator";
import EnginePicker from "../components/EnginePicker";
import useDragPanel from "../utils/useDragPanel";
import { dragOutProps, contextShareProps } from "../utils/dragOut";
import "./FotosVerarbeitenPage.css";

/* ══════════════════════════════════════════════════════════════
   API Helpers
   ══════════════════════════════════════════════════════════════ */
const api = (path, opts = {}) =>
  fetch(API_BASE + "/zuordnen/api" + path, {
    headers: { "Content-Type": "application/json", ...opts.headers },
    ...opts,
  }).then(async (r) => {
    if (!r.ok) {
      let detail = "";
      try { const j = await r.json(); detail = j.detail || ""; } catch {}
      throw new Error("API " + r.status + (detail ? ": " + detail : ""));
    }
    return r.json();
  });

/* Welle 84: Auto-Ziel "…/#Datum/101 - 2020-02-21" → Hinweis "…/#Datum/101 - <je Datei>" fürs Panel */
const _zielBasisHinweis = (ziel) => {
  const m = (ziel || "").match(/^(.*\/#?[Dd]atum)\/(.+?)\s+-\s+\d{4}-\d{2}-\d{2}$/);
  return m ? `${m[1]}/${m[2]} - <je Datei>` : (ziel || "");
};

const apiPost = (path, body) =>
  fetch(API_BASE + "/zuordnen/api" + path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(async (r) => {
    if (!r.ok) {
      let detail = "";
      try { const j = await r.json(); detail = j.detail || ""; } catch {}
      throw new Error("API " + r.status + (detail ? ": " + detail : ""));
    }
    return r.json();
  });

/* ══════════════════════════════════════════════════════════════
   Confidence Config
   ══════════════════════════════════════════════════════════════ */
const CONF = {
  green:    { bg: "#0d3320", border: "#2e7d32", text: "#81c784", label: "Sicher (≤100m)",  icon: "●" },
  yellow:   { bg: "#3e2723", border: "#e65100", text: "#ffb74d", label: "Möglich (≤250m)", icon: "◐" },
  red:      { bg: "#3b1010", border: "#c62828", text: "#ef9a9a", label: "Kein Match",       icon: "○" },
  duplikat: { bg: "#2a1a3e", border: "#7b1fa2", text: "#ce93d8", label: "Duplikat",         icon: "⊘" },
  none:     { bg: "#1a1a2e", border: "#444",    text: "#888",    label: "Ohne Status",      icon: "–" },
};

const GROUP_ORDER = [
  { key: "green",    label: "Sicher (≤100m)",  icon: "●" },
  { key: "yellow",   label: "Möglich (≤250m)", icon: "◐" },
  { key: "red",      label: "Kein Match",       icon: "○" },
  { key: "duplikat", label: "Duplikat",         icon: "⊘" },
  { key: "none",     label: "Ohne Status",      icon: "–" },
];

/* ══════════════════════════════════════════════════════════════
   Hilfsfunktionen
   ══════════════════════════════════════════════════════════════ */
// Welle 12 — baut den Präfix-Teil mit optionaler Einheit + Raum:
//   006        → "006"           (default)
//   006 + 006-01      → "006-01"
//   006 + 006-01 + Bad → "006-01-Bad"
//   006 + ""    + Bad → "006-Bad"
// Einheit kommt im Format "006-01" rein (so wie sie /fotos/einheiten liefert),
// wir nehmen nur den Teil nach dem letzten "-" als Kurzform.
function _buildPrefixPart(shortPrefix, einheit, raum) {
  let part = (shortPrefix || "").trim();
  const einheitKurz = einheit ? String(einheit).split("-").pop().trim() : "";
  if (einheitKurz) part = part ? `${part}-${einheitKurz}` : einheitKurz;
  const raumClean = (raum || "").trim();
  if (raumClean) part = part ? `${part}-${raumClean}` : raumClean;
  return part;
}

function _buildFilename(prefix, fotoDatum, origName, annotation, ext, einheit, raum, fotoZeit) {
  // prefix: nur Objekt-Kürzel (z.B. "005"), ohne " Geodaten" etc.
  const shortPrefix = (prefix || "").replace(/\s+geodaten$/i, "").trim();
  let baseName = (origName || "").replace(/\.[^.]+$/, "").trim();
  // Wiederholtes Vornher-Strippen aller Bestandteile, die im Original-
  // Namen bereits vorkommen — sonst landen sie doppelt im neuen Namen.
  const einheitKurz = einheit ? String(einheit).split("-").pop().trim() : "";
  const stripTokens = [shortPrefix, einheit, einheitKurz, raum, fotoDatum]
    .map(t => (t || "").trim()).filter(Boolean);
  // Regex-Fallback: führendes ISO-Datum (z.B. "2026-05-27") und führende
  // 1–2-stellige Zahl (alte Einheit, z. B. "01"); letztere nur wenn der
  // User explizit eine neue Einheit gesetzt hat — sonst wäre "12" am
  // Anfang eines Bildnamens fälschlich gestrippt.
  const datumRegex = /^\d{4}-\d{2}-\d{2}\b/;
  const altUnitRegex = /^\d{1,2}\b/;
  let changed = true;
  while (changed) {
    changed = false;
    for (const tok of stripTokens) {
      if (tok && baseName.startsWith(tok)) {
        baseName = baseName.slice(tok.length).replace(/^[\s._-]+/, "").trim();
        changed = true;
      }
    }
    if (datumRegex.test(baseName)) {
      baseName = baseName.replace(datumRegex, "").replace(/^[\s._-]+/, "").trim();
      changed = true;
    }
    if (einheitKurz && altUnitRegex.test(baseName)) {
      baseName = baseName.replace(altUnitRegex, "").replace(/^[\s._-]+/, "").trim();
      changed = true;
    }
  }
  // Welle 63: Uhrzeit (HH.MM.SS) aus fotoZeit ("YYYY-MM-DD HH:MM:SS" oder "HH:MM:SS")
  // — verhindert Kollisionen (kein "… (1)" mehr). Bei vorhandener Uhrzeit wird der
  // nichtssagende Original-Name/Zähler weggelassen.
  let zeitPart = "";
  const zm = String(fotoZeit || "").match(/(\d{2}):(\d{2}):(\d{2})/);
  if (zm) zeitPart = `${zm[1]}.${zm[2]}.${zm[3]}`;
  const prefixPart = _buildPrefixPart(shortPrefix, einheit, raum);
  const parts = [prefixPart, fotoDatum, zeitPart, (zeitPart ? "" : baseName), annotation].filter(Boolean);
  return (parts.join(" - ") || "bild") + ext;
}

// Trello-Titel aus dem (bereits korrekt zusammengesetzten) Dateinamen
// bilden — Datum und Endung entfernen, den Präfix-Block (alles vor dem
// ersten „ - ") inklusive seiner Bindestriche bewahren, ab dem zweiten
// Token die Trenner ` - ` zu schlichten Leerzeichen verdichten.
// Beispiel: „006-04-Bad - 2026-05-27 - 849 - test.jpeg"
//   → „006-04-Bad 849 test"
function _titleFromFilename(filename) {
  const stripped = (filename || "")
    .replace(/\.\w{2,4}$/i, "")
    .replace(/\b\d{4}-\d{1,2}-\d{1,2}\b/g, "")
    .replace(/\b\d{1,2}\.\d{1,2}\.\d{2,4}\b/g, "")
    .replace(/\s*-\s*-\s*/g, " - ")
    .replace(/^\s*-\s*|\s*-\s*$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  // Erst-Token (Präfix-Block) behalten, danach ` - ` → ` `
  const splitIdx = stripped.indexOf(" - ");
  if (splitIdx < 0) return stripped;
  const head = stripped.slice(0, splitIdx).trim();
  const tail = stripped.slice(splitIdx + 3).replace(/\s*-\s*/g, " ").replace(/\s{2,}/g, " ").trim();
  return tail ? `${head} ${tail}` : head;
}


/* ══════════════════════════════════════════════════════════════
   DuplikatModal – Thumbnail-Viewer mit Dateigröße + Zoom
   ══════════════════════════════════════════════════════════════ */
function _fmtSize(bytes) {
  if (!bytes) return "?";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function DuplikatModal({ info, apiBase }) {
  const [quellThumb, setQuellThumb] = React.useState(null);
  const [existThumb, setExistThumb] = React.useState(null);
  const [zoomed, setZoomed] = React.useState(null); // 'quell' | 'exist' | null

  React.useEffect(() => {
    setQuellThumb(null); setExistThumb(null); setZoomed(null);
    // Thumbnails parallel laden
    const load = async (path, setter) => {
      try {
        const res = await fetch(apiBase + "/zuordnen/api/fotos/thumbnail?path=" + encodeURIComponent(path) + "&max_size=400");
        if (res.ok) { const d = await res.json(); setter(d.image || null); }
      } catch {}
    };
    if (info.quell?.path) load(info.quell.path, setQuellThumb);
    if (info.existierend?.path) load(info.existierend.path, setExistThumb);
  }, [info.quell?.path, info.existierend?.path, apiBase]);

  return (
    <div className="v-overlay" onClick={() => !zoomed && info.onAbort()}>
      {zoomed && (
        <div className="v-overlay" style={{ zIndex: 9999, background: "rgba(0,0,0,0.92)" }}
          onClick={() => setZoomed(null)}>
          <img
            src={"data:image/jpeg;base64," + (zoomed === 'quell' ? quellThumb : existThumb)}
            alt=""
            style={{ maxWidth: "92vw", maxHeight: "88vh", objectFit: "contain", borderRadius: 8 }}
          />
          <div style={{ color: "#fff", marginTop: 12, fontSize: "0.85em", opacity: 0.7 }}>Klicken zum Schließen</div>
        </div>
      )}
      <div className="v-modal v-duplikat-modal" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
        <div className="v-modal-header">
          <h3>⚠️ Duplikat gefunden</h3>
          <button className="v-dismiss" onClick={() => info.onAbort()}>×</button>
        </div>
        <p className="v-modal-desc">Diese Datei existiert bereits im Bilder-Ordner.</p>
        <div className="v-duplikat-details" style={{ display: "flex", gap: 16 }}>
          {/* Neue Datei (Quelle) */}
          <div className="v-duplikat-file v-duplikat-source" style={{ flex: 1 }}>
            <div className="v-duplikat-label" style={{ color: "#81c784" }}>Neu (wird verschoben)</div>
            <div className="v-duplikat-name">{info.quell?.name}</div>
            <div className="v-duplikat-size" style={{ color: "#aaa", fontSize: "0.82em", marginBottom: 6 }}>
              {_fmtSize(info.quell?.size)}{info.quell?.modified ? ` · ${info.quell.modified}` : ""}
            </div>
            <div className="v-duplikat-thumb" style={{ position: "relative", cursor: quellThumb ? "zoom-in" : "default" }}
              onClick={() => quellThumb && setZoomed('quell')}>
              {quellThumb
                ? <img src={"data:image/jpeg;base64," + quellThumb} alt="" style={{ width: "100%", borderRadius: 6, display: "block" }} />
                : <div style={{ height: 140, background: "#2a2a2a", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "#555" }}>Lade...</div>
              }
              {quellThumb && <div style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.5)", borderRadius: 4, padding: "2px 5px", fontSize: "0.75em", color: "#fff" }}>🔍</div>}
            </div>
          </div>
          {/* Bestehende Datei */}
          <div className="v-duplikat-file v-duplikat-existing" style={{ flex: 1 }}>
            <div className="v-duplikat-label" style={{ color: "#ef9a9a" }}>Alt (bereits vorhanden)</div>
            <div className="v-duplikat-name">{info.existierend?.name}</div>
            <div className="v-duplikat-size" style={{ color: "#aaa", fontSize: "0.82em", marginBottom: 6 }}>
              {_fmtSize(info.existierend?.size)}{info.existierend?.modified ? ` · ${info.existierend.modified}` : ""}
            </div>
            <div className="v-duplikat-thumb" style={{ position: "relative", cursor: existThumb ? "zoom-in" : "default" }}
              onClick={() => existThumb && setZoomed('exist')}>
              {existThumb
                ? <img src={"data:image/jpeg;base64," + existThumb} alt="" style={{ width: "100%", borderRadius: 6, display: "block" }} />
                : <div style={{ height: 140, background: "#2a2a2a", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "#555" }}>Lade...</div>
              }
              {existThumb && <div style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.5)", borderRadius: 4, padding: "2px 5px", fontSize: "0.75em", color: "#fff" }}>🔍</div>}
            </div>
          </div>
        </div>
        <div className="v-modal-actions" style={{ marginTop: 16 }}>
          <button className="v-btn primary" onClick={() => info.onReplace()}>
            <FaArrowRight style={{ marginRight: 6 }} />Alte ersetzen
          </button>
          <button className="v-btn danger" onClick={() => info.onDelete()}>
            <FaTrash style={{ marginRight: 6 }} />Neue löschen
          </button>
          <button className="v-btn" style={{ background: "#0277bd" }} onClick={() => info.onKeepBoth()}>
            Beide behalten
          </button>
          <button className="v-btn ghost" onClick={() => info.onAbort()}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Komponente
   ══════════════════════════════════════════════════════════════ */
export default function FotosVerarbeitenPage() {
  const navigate = useNavigate();
  const location = useLocation();

  /* ── Phase & Dateiliste ──────────────────────────────────── */
  const [phase, setPhase] = useState("loading");
  const [dateien, setDateien] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [inbox, setInbox] = useState("");
  const [sourceFolder, setSourceFolder] = useState(location.state?.ordner || "");
  /* Optional: nur bestimmte Dateien laden (z.B. von KomprimierungPage) */
  const [nurDateien] = useState(location.state?.nurDateien || null);

  /* ── Datei-Settings ─────────────────────────────────────── */
  // { [path]: { prefix, foto_datum, annotation, ziel_ordner, confidence, reason,
  //             kopie_nok, nok_original, trello_enabled, trello_mode, trello_grund,
  //             _geodaten: {lat, lon, alt, adresse, geo_config_id} | null } }
  const [fileSettings, setFileSettings] = useState({});
  const [lockedGroups, setLockedGroups]  = useState({});  // {[path]: confidence} – eingefroren nach Analyse

  /* ── Vorschau ────────────────────────────────────────────── */
  const [thumbnail, setThumbnail]     = useState(null);
  const [thumbLoading, setThumbLoading] = useState(false);
  const thumbCache = useRef({});

  /* ── Browse ──────────────────────────────────────────────── */
  const [browseOpen, setBrowseOpen]     = useState(false);
  const [browsePath, setBrowsePath]     = useState("");
  const [browseItems, setBrowseItems]   = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const browseTargetPath = useRef(null);

  /* ── Analyse ─────────────────────────────────────────────── */
  const [enginePickerFile, setEnginePickerFile] = useState(null); // Welle 80: gemeinsamer Picker
  // Welle 77: „Analyse läuft" = ein GPS-Analyse-Job ist aktiv (abgeleitet, s. gpsJobs)
  const [analysingPaths, setAnalysingPaths] = useState(new Set());

  /* ── Move-Status ─────────────────────────────────────────── */
  const [moving, setMoving]       = useState(false);
  const [inFlightPaths, setInFlightPaths] = useState(new Set()); // Dateien in laufenden Verschiebe-Jobs
  const [jobVorlauf, setJobVorlauf] = useState(""); // z. B. "Duplikat-Prüfung läuft…" vor dem Job-Start
  const { dragOffset: browseDrag, onDragStart: onBrowseDragStart } = useDragPanel(); // Ordner-Browser verschiebbar
  const [nachZaehlerPath, setNachZaehlerPath] = useState(null);
  const [moveResult, setMoveResult] = useState(null);

  /* ── Duplikat-Check ──────────────────────────────────────── */
  const [duplikatInfo, setDuplikatInfo] = useState(null);
  const [duplikatChecking, setDuplikatChecking] = useState(false);

  /* ── Batch-Confirm Dialog ────────────────────────────────── */
  const [showBatch, setShowBatch] = useState(false);
  const [batchCandidates, setBatchCandidates] = useState([]);
  const [batchMoving, setBatchMoving] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [batchProgress, setBatchProgress] = useState({});

  /* ── Modus: "verschieben" (Standard) | "umbenennen" (nur Rename im selben Ordner) ──
     Herkunft bestimmt den Start-Modus: von der Homepage → immer "verschieben",
     von Bilder-Komprimieren → per location.state "umbenennen". Der zuletzt
     benutzte Modus wird bewusst NICHT gemerkt. */
  const [processMode, setProcessMode] = useState(() =>
    location.state?.modus || "verschieben"
  );
  const isUmbenennen = processMode === "umbenennen";

  /* ── Nachrichten-Bar ─────────────────────────────────────── */
  const [statusMsg, setStatusMsg] = useState("");

  /* ── Welle 12: Räume-Stammdaten (objekt_zimmer) ───────────── */
  const [zimmerList, setZimmerList] = useState([]);
  const loadZimmer = useCallback(async () => {
    try {
      const r = await api("/fotos/zimmer");
      setZimmerList((r?.zimmer || []).map(z => z.name));
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadZimmer(); }, [loadZimmer]);
  // Neuen Raum-Stammdatensatz anlegen, wenn der User ein freies Wort eingetragen hat,
  // das noch nicht in der Liste steht. Idempotent — Server reaktiviert ggf. soft-deleted.
  const persistNewRaum = useCallback(async (name) => {
    const clean = (name || "").trim();
    if (!clean) return;
    if (zimmerList.some(n => n.toLowerCase() === clean.toLowerCase())) return;
    try {
      await api("/fotos/zimmer", { method: "POST", body: JSON.stringify({ name: clean }) });
      setZimmerList(prev => prev.includes(clean) ? prev : [...prev, clean]);
    } catch { /* ignore */ }
  }, [zimmerList]);

  /* ── Barcode Auto-Move ───────────────────────────────────── */
  const [barcodeMovedCount, setBarcodeMovedCount] = useState(0);
  const barcodeTimerRef = useRef(null);

  /* ── Trello Popup ────────────────────────────────────────── */
  const [trelloPopupOpen, setTrelloPopupOpen] = useState(false);
  const [trelloPopupPath, setTrelloPopupPath] = useState(null);
  const [trelloGrund, setTrelloGrund]         = useState("");
  const [trelloTitel, setTrelloTitel]         = useState("");
  const [trelloSaving, setTrelloSaving]       = useState(false);
  const [trelloError, setTrelloError]         = useState("");
  const [trelloSuccess, setTrelloSuccess]     = useState("");
  const trelloBeimVerschiebenRef = useRef(null);

  // Welle 40: Anhang-Popup (Kopie ins Rechnungs-Dateianhang-Verzeichnis)
  const [anhangPopupOpen, setAnhangPopupOpen] = useState(false);
  const [anhangPopupPath, setAnhangPopupPath] = useState(null);
  const [anhangNummer, setAnhangNummer]       = useState("");
  const [anhangMassMode, setAnhangMassMode]   = useState(false); // Welle 64: eine Nummer für alle markierten
  const anhangInputRef = useRef(null);

  const openAnhangPopup = (path) => {
    const s = fileSettings[path] || {};
    setAnhangMassMode(false);
    setAnhangPopupPath(path);
    setAnhangNummer(s.anhang_nummer || "");
    setAnhangPopupOpen(true);
    setTimeout(() => anhangInputRef.current?.focus(), 60);
  };

  // Welle 64 (SIJ-665): Anhang als Massenaktion — ein Popup, Nummer für alle markierten
  const openAnhangPopupMass = () => {
    if (checkedPaths.size === 0) return;
    setAnhangMassMode(true);
    setAnhangPopupPath(null);
    setAnhangNummer("");
    setAnhangPopupOpen(true);
    setTimeout(() => anhangInputRef.current?.focus(), 60);
  };

  const bestaetigeAnhang = () => {
    const nummer = anhangNummer.trim();
    if (!nummer) return;
    if (anhangMassMode) {
      // Massen-Modus: gleiche Nummer auf alle markierten Karten
      setFileSettings((prev) => {
        const next = { ...prev };
        for (const p of checkedPaths) {
          next[p] = { ...next[p], anhang_enabled: true, anhang_nummer: nummer };
        }
        return next;
      });
      setAnhangMassMode(false);
      setAnhangPopupOpen(false);
      return;
    }
    if (!anhangPopupPath) return;
    setFileSettings((prev) => ({
      ...prev,
      [anhangPopupPath]: { ...prev[anhangPopupPath], anhang_enabled: true, anhang_nummer: nummer },
    }));
    setAnhangPopupOpen(false);
  };

  // Vorschau des entstehenden Anhang-Dateinamens (Logik wie im Backend)
  const anhangPrefixPreview = (nummer) => {
    const roh = (nummer || "").trim().toUpperCase();
    const digits = roh.replace(/[^0-9]/g, "").replace(/^0+/, "");
    if (!digits) return "";
    return (roh.startsWith("AN") ? "AN" : "RE") + digits;
  };

  /* ── Filter ──────────────────────────────────────────────── */
  const [filterPrefix, setFilterPrefix] = useState("");
  const [filterDatum, setFilterDatum] = useState("");
  const [filterText, setFilterText] = useState("");

  /* ── Markierung ──────────────────────────────────────────── */
  const [checkedPaths, setCheckedPaths] = useState(new Set());
  const lastCheckedRef = useRef(null);   // für Shift-Klick-Bereichsauswahl

  // Welle 64 (SIJ-665): Trello als Massenaktion — kein Popup, Karte automatisch
  // beim Verschieben. (Muss NACH checkedPaths deklariert sein.)
  const alleMarkiertenTrelloAn = checkedPaths.size > 0 &&
    [...checkedPaths].every((p) => fileSettings[p]?.trello_enabled);
  const toggleTrelloMass = () => {
    if (checkedPaths.size === 0) return;
    const anschalten = !alleMarkiertenTrelloAn;
    setFileSettings((prev) => {
      const next = { ...prev };
      for (const p of checkedPaths) {
        next[p] = anschalten
          ? { ...next[p], trello_enabled: true, trello_mode: "beim_verschieben" }
          : { ...next[p], trello_enabled: false };
      }
      return next;
    });
  };

  /* ── Welle 25: kleine Thumbnails je Karte ────────────────── */
  const [rowThumbs, setRowThumbs] = useState({});  // { [path]: base64 | "failed" }
  const [previewPath, setPreviewPath] = useState(null); // großes Bild NUR nach Klick aufs Thumbnail
  const rowThumbsRef = useRef({});                  // synchroner Spiegel für die Lade-Queue
  const visiblePathsRef = useRef(new Set());        // aktuell sichtbare Karten (Priorität)
  const thumbObserverRef = useRef(null);

  /* ── Welle 25: Anwenden-Leiste (Werte auf Auswahl übertragen) ── */
  const [applyBar, setApplyBar] = useState({
    prefix: "", einheit: "", raum: "", foto_datum: "", annotation: "", ziel_ordner: "",
  });

  /* ── Einheiten-Dropdown ──────────────────────────────────── */
  const einheitenCache = useRef({});  // { [objekt]: ["005-01", "005-06", ...] }
  const [einheitenOpen, setEinheitenOpen] = useState({});  // { [path]: true }
  const [einheitenList, setEinheitenList] = useState({});   // { [path]: [...] }

  /* ── Prefix-Suggest ─────────────────────────────────────── */
  const prefixSuggestCache = useRef({});  // { [q]: [{prefix, pfad, adresse}] }
  const [prefixSuggest, setPrefixSuggest] = useState({});  // { [path]: [{...}] }
  const prefixSuggestTimer = useRef(null);

  /* ── Manuell verschieben ─────────────────────────────────── */
  const [nachManuellPath, setNachManuellPath] = useState(null);

  /* ── Zeit prüfen ─────────────────────────────────────────── */
  const [zeitChecking, setZeitChecking] = useState(false);

  /* ── Pfad kopiert Feedback ───────────────────────────────── */
  const [copiedPath, setCopiedPath] = useState(null);  // path key for ✓ feedback

  /* ── GeoConfig Modal ─────────────────────────────────────── */
  // { dateiPath, lat, lon, adresse } | null
  const [geoConfigModal, setGeoConfigModal] = useState(null);

  /* ── Bild-Viewer ─────────────────────────────────────────── */
  const [imgRotation, setImgRotation] = useState(0);
  const [imgZoom, setImgZoom]         = useState(1);
  const [imgPan, setImgPan]           = useState({ x: 0, y: 0 });
  const imgDragRef = useRef(null);

  /* ── Welle 25: schwebendes Bild-Fenster (Position + Drag) ── */
  const [floatPos, setFloatPos] = useState(() => ({
    x: Math.max(20, window.innerWidth - 640),
    y: 120,
  }));
  const floatDragRef = useRef(null);
  const floatImgAreaRef = useRef(null);
  const startFloatDrag = useCallback((e) => {
    if (e.target.closest("button")) return; // Kopf-Buttons nicht draggen
    floatDragRef.current = { dx: e.clientX, dy: e.clientY };
    const startPos = { ...floatPos };
    const move = (ev) => {
      setFloatPos({
        x: startPos.x + (ev.clientX - floatDragRef.current.dx),
        y: Math.max(0, startPos.y + (ev.clientY - floatDragRef.current.dy)),
      });
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }, [floatPos]);

  const listRef  = useRef(null);
  const cardRefs = useRef({});
  const lockedGroupsRef = useRef({});  // Spiegelt lockedGroups für synchronen Zugriff in Callbacks

  /* ═══════════════════════════════════════════════════════════
     Helpers
     ═══════════════════════════════════════════════════════════ */
  const shortPath = (p) => {
    if (!p) return "";
    const idx = p.indexOf("Holthey Immobilien/");
    return idx >= 0 ? p.substring(idx + 19) : p;
  };

  const updateZiel = useCallback((path, ziel) => {
    setFileSettings((prev) => ({
      ...prev,
      [path]: { ...prev[path], ziel_ordner: ziel, _ziel_auto: false },   // Welle 84: von Hand → kein Auto-Wert mehr
    }));
  }, []);

  const getSetting = useCallback((path) =>
    fileSettings[path] || {
      prefix: "", foto_datum: "", annotation: "", ziel_ordner: "",
      confidence: "none", reason: "", kopie_nok: false, nok_original: false,
      trello_enabled: false, trello_mode: "", trello_grund: "",
      _geodaten: null, orig_name_edit: "", ziel_ist_neu: false,
    },
  [fileSettings]);

  const updateSetting = useCallback((path, key, val) => {
    setFileSettings((prev) => {
      const upd = { ...(prev[path] || {}), [key]: val };
      // Von Hand geändert → gilt nicht mehr als GPS-Automatik-Wert
      if (key === "prefix") upd._prefix_auto = false;
      if (key === "ziel_ordner") upd._ziel_auto = false;
      return { ...prev, [path]: upd };
    });
  }, []);

  /* Prefix aufteilen: tippt der User "006-01" ins Prefix-Feld, sind die ersten
     3 Stellen das Objekt und der Rest die Einheit. _prefixTeile liefert
     {basis, einheit} oder null, wenn nichts aufzuteilen ist. */
  const _prefixTeile = (val) => {
    const m = (val || "").trim().match(/^(\d{3}|\d{2,3}[A-Za-z])[-_ ]+(.+)$/);
    return m ? { basis: m[1], einheit: m[2].trim() } : null;
  };
  // Für Ordner-Suche/Vorschläge zählt immer nur das Basis-Objekt (erste 3 Stellen)
  const _prefixBasis = (val) => {
    const t = _prefixTeile(val);
    return t ? t.basis : (val || "").trim();
  };

  /* Prefix-Lookup: nach Eingabe den Ziel-Ordner automatisch setzen */
  const prefixLookupTimer = useRef(null);
  const prefixQueryRef = useRef({}); // { [path]: zuletzt getippter Wert } — gegen veraltete Antworten
  const updatePrefix = useCallback((path, val) => {
    // Sofort prefix im State setzen (von Hand → kein GPS-Automatik-Wert mehr)
    setFileSettings((prev) => ({
      ...prev,
      [path]: { ...(prev[path] || {}), prefix: val, _prefix_auto: false },
    }));
    // Debounced Suggest + Lookup (300ms)
    if (prefixLookupTimer.current) clearTimeout(prefixLookupTimer.current);
    const trimmed = (val || "").trim();
    prefixQueryRef.current[path] = trimmed;
    if (!trimmed) {
      setPrefixSuggest((prev) => ({ ...prev, [path]: [] }));
      return;
    }
    // Ordner-Suche/Vorschläge laufen immer über das Basis-Objekt — auch wenn
    // der User "006-01" tippt, wird der Ordner von 006 gefunden
    const suchwert = _prefixBasis(trimmed);
    prefixLookupTimer.current = setTimeout(async () => {
      // Prefix-Vorschläge aus geodaten_config
      try {
        const cacheKey = suchwert.toLowerCase();
        let vorschlaege = prefixSuggestCache.current[cacheKey];
        if (!vorschlaege) {
          const res = await api("/fotos/prefix-suggest?q=" + encodeURIComponent(suchwert));
          vorschlaege = res.vorschlaege || [];
          prefixSuggestCache.current[cacheKey] = vorschlaege;
        }
        // Veraltete Antwort verwerfen: nur anzeigen, wenn die Eingabe noch
        // dem angefragten Wert entspricht (sonst überschreibt z. B. die
        // langsame "0"-Antwort die bereits angezeigte "08"-Liste).
        if (prefixQueryRef.current[path] === trimmed) {
          setPrefixSuggest((prev) => ({ ...prev, [path]: vorschlaege }));
        }
      } catch {}
      // Ziel-Ordner Lookup (ebenfalls über das Basis-Objekt)
      try {
        const s = fileSettings[path] || {};
        const datum = s.foto_datum || "";
        const res = await api("/fotos/prefix-lookup?prefix=" + encodeURIComponent(suchwert) + "&foto_datum=" + encodeURIComponent(datum));
        if (res.ziel_ordner) {
          setFileSettings((prev) => {
            const cur = prev[path] || {};
            if ((cur.prefix || "").trim() !== trimmed) return prev;
            return {
              ...prev,
              [path]: { ...cur, ziel_ordner: res.ziel_ordner, ziel_ist_neu: res.ziel_ist_neu || false, confidence: cur.confidence === "red" ? "yellow" : cur.confidence },
            };
          });
        }
      } catch {}
    }, 300);
  }, [fileSettings]);

  /* Prefix bestätigen (Enter/Feld verlassen): "006-01" wird automatisch
     aufgeteilt — Prefix bleibt "006", die "01" wandert ins Einheit-Feld. */
  const commitPrefix = useCallback((path) => {
    setFileSettings((prev) => {
      const cur = prev[path] || {};
      const teile = _prefixTeile(cur.prefix);
      if (!teile) return prev;
      return {
        ...prev,
        [path]: { ...cur, prefix: teile.basis, einheit: teile.einheit, _prefix_auto: false },
      };
    });
  }, []);

  /* Prefix-Vorschlag auswählen: prefix setzen, wenn pfad vorhanden direkt übernehmen */
  const selectPrefixSuggest = useCallback((path, vorschlag) => {
    setPrefixSuggest((prev) => ({ ...prev, [path]: [] }));
    prefixQueryRef.current[path] = null; // offene Suggest-Antworten verwerfen
    const newPrefix = vorschlag.prefix;
    setFileSettings((prev) => {
      const cur = prev[path] || {};
      const updated = { ...cur, prefix: newPrefix, _prefix_auto: false };
      if (vorschlag.pfad) {
        updated.ziel_ordner = vorschlag.pfad;
        updated.ziel_ist_neu = false;
        updated._ziel_auto = false;
      }
      return { ...prev, [path]: updated };
    });
    // Ziel-Ordner nachladen wenn kein expliziter Pfad
    if (!vorschlag.pfad) {
      if (prefixLookupTimer.current) clearTimeout(prefixLookupTimer.current);
      prefixLookupTimer.current = setTimeout(async () => {
        try {
          const s = fileSettings[path] || {};
          const datum = s.foto_datum || "";
          const res = await api("/fotos/prefix-lookup?prefix=" + encodeURIComponent(newPrefix) + "&foto_datum=" + encodeURIComponent(datum));
          if (res.ziel_ordner) {
            setFileSettings((prev) => ({
              ...prev,
              [path]: { ...(prev[path] || {}), ziel_ordner: res.ziel_ordner, ziel_ist_neu: res.ziel_ist_neu || false },
            }));
          }
        } catch {}
      }, 50);
    }
  }, [fileSettings]);

  /* Einheiten für ein Objekt laden (aus adressen-Tabelle) */
  const loadEinheiten = useCallback(async (path, objekt) => {
    if (einheitenCache.current[objekt]) {
      setEinheitenList(prev => ({ ...prev, [path]: einheitenCache.current[objekt] }));
      setEinheitenOpen(prev => ({ ...prev, [path]: true }));
      return;
    }
    try {
      const res = await api("/fotos/einheiten?objekt=" + encodeURIComponent(objekt));
      const list = res.einheiten || [];
      einheitenCache.current[objekt] = list;
      setEinheitenList(prev => ({ ...prev, [path]: list }));
      setEinheitenOpen(prev => ({ ...prev, [path]: true }));
    } catch { /* ignore */ }
  }, []);

  const selectEinheit = useCallback((path, einheit) => {
    setEinheitenOpen(prev => ({ ...prev, [path]: false }));
    updatePrefix(path, einheit);
  }, [updatePrefix]);

  /* Zeit prüfen: zeitnahe Fotos übernehmen Prefix + Pfad des aktiven Fotos */
  const handleZeitCheck = useCallback(async () => {
    if (selectedIdx < 0 || !dateien[selectedIdx]) return;
    const sel = dateien[selectedIdx];
    const s = fileSettings[sel.path] || {};
    if (!s.prefix) { setStatusMsg("⚠ Bitte erst Prefix setzen"); return; }
    setZeitChecking(true);
    try {
      const res = await api("/fotos/zeitnah?foto_name=" + encodeURIComponent(sel.name) + "&minuten=15");
      const matches = (res.fotos || []).filter(f =>
        dateien.some(d => d.name === f.foto_name)
      );
      if (matches.length === 0) {
        setStatusMsg("⏱ 0 zeitnahe Fotos gefunden (±15min)");
        return;
      }
      setFileSettings(prev => {
        const next = { ...prev };
        for (const m of matches) {
          const datei = dateien.find(d => d.name === m.foto_name);
          if (!datei) continue;
          const cur = next[datei.path] || {};
          const ext = "." + datei.name.split(".").pop().toLowerCase();
          next[datei.path] = {
            ...cur,
            prefix: s.prefix,
            ziel_ordner: s.ziel_ordner || cur.ziel_ordner || "",
            ziel_ist_neu: s.ziel_ist_neu || false,
            dateiname_neu: _buildFilename(s.prefix, cur.foto_datum || s.foto_datum, cur.orig_name_edit || datei.name, cur.annotation, ext, cur.einheit, cur.raum, cur.erstellt_am || s.erstellt_am),
          };
        }
        return next;
      });
      setStatusMsg(`✓ ${matches.length} Foto${matches.length > 1 ? "s" : ""} übernommen (±15min)`);
    } catch (e) {
      setStatusMsg("❌ Zeit-Check fehlgeschlagen: " + (e.message || e));
    } finally {
      setZeitChecking(false);
    }
  }, [selectedIdx, dateien, fileSettings]);

  /* ═══════════════════════════════════════════════════════════
     Welle 77: Analyse-Ergebnis in die Karte übernehmen
     (gemeinsam für Einzel-Analyse, Cache-Laden und Job-Ergebnisse)
     ═══════════════════════════════════════════════════════════ */
  const uebernehmeAnalyse = useCallback((pfad, r) => {
    if (!r) return;
    // Barcode erkannt → Datei wurde automatisch in Zähler-Inbox verschoben
    if (r.barcode_moved) {
      setDateien((prev) => prev.filter((d) => d.path !== pfad));
      setFileSettings((prev) => { const n = { ...prev }; delete n[pfad]; return n; });
      setBarcodeMovedCount((c) => {
        const neu = c + 1;
        if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
        barcodeTimerRef.current = setTimeout(() => setBarcodeMovedCount(0), 8000);
        return neu;
      });
      return;
    }
    const fname = pfad.split("/").pop() || "";
    const mName = fname.match(/(\d{4})[._-](\d{2})[._-](\d{2})/);
    setFileSettings((prev) => ({
      ...prev,
      [pfad]: {
        ...(prev[pfad] || {}),
        prefix:     r.prefix  || "",
        _prefix_auto: !!r.prefix,
        _ziel_auto:   isUmbenennen ? false : !!r.ziel_ordner,
        foto_datum: r.foto_datum || (mName ? `${mName[1]}-${mName[2]}-${mName[3]}` : ""),
        _datum_from_name: !r.foto_datum && !!mName,
        annotation: prev[pfad]?.annotation || "",
        ziel_ordner: isUmbenennen
          ? (prev[pfad]?.ziel_ordner || pfad.substring(0, pfad.lastIndexOf("/")))
          : (r.ziel_ordner || ""),
        ziel_ist_neu: isUmbenennen ? false : (r.ziel_ist_neu || false),
        confidence: r.confidence || "none",
        reason:     r.reason    || "",
        erstellt_am: r.erstellt_am || null,
        _geodaten: r.latitude != null ? {
          lat: r.latitude, lon: r.longitude, alt: r.altitude,
          adresse: r.adresse || "", geo_config_id: r.geo_config_id,
        } : null,
      },
    }));
    setLockedGroups((prev) => ({ ...prev, [pfad]: r.confidence || "none" }));
    lockedGroupsRef.current = { ...lockedGroupsRef.current, [pfad]: r.confidence || "none" };
  }, [isUmbenennen]);

  /* GPS-Analyse-Job (Backend analysiert Bild für Bild, wir zeigen jedes
     Ergebnis sofort — kein „alles erst durch, dann anzeigen" mehr). */
  const onGpsItemResult = useCallback((item) => {
    if (!item) return;
    if (item.ok) uebernehmeAnalyse(item.pfad, item);
    else if (item.fehler) setStatusMsg(`Analyse-Fehler ${item.name || ""}: ${item.fehler}`);
  }, [uebernehmeAnalyse]);
  const { jobs: gpsJobs, registerJob: registerGpsJob, dismissJob: dismissGpsJob } = useVerschiebenJobs({
    pageKey: "fotos_gps",
    onItemResult: onGpsItemResult,
    onJobDone: (job) => {
      setStatusMsg(job.status === "fertig"
        ? `Analyse fertig: ${job.ok_count || 0} Bilder${job.fehler_count ? `, ${job.fehler_count} Fehler` : ""}`
        : `Analyse ${job.status}`);
      setTimeout(() => dismissGpsJob(job.job_id), 4000);
    },
  });
  const gpsJobList = Object.values(gpsJobs).filter((j) => j.status === "running");
  const analyseRunning = gpsJobList.length > 0;
  const gpsFortschritt = gpsJobList.reduce(
    (acc, j) => ({ done: acc.done + (j.done || 0), total: acc.total + (j.total || 0), current: j.current_datei || acc.current }),
    { done: 0, total: 0, current: "" }
  );

  const startGpsJob = useCallback(async (pfade, force = false) => {
    const liste = (pfade || []).filter(Boolean);
    if (liste.length === 0) return null;
    try {
      const d = await api("/fotos/analyse-job", {
        method: "POST",
        body: JSON.stringify({ pfade: liste, force }),
      });
      if (d?.job_id) {
        registerGpsJob(d.job_id, d.total || liste.length);
        setAnalysingPaths((prev) => new Set([...prev, ...liste]));
        setStatusMsg(`GPS-Analyse gestartet: ${liste.length} Bild${liste.length > 1 ? "er" : ""}`);
      }
      return d?.job_id || null;
    } catch (e) {
      setStatusMsg("Analyse konnte nicht gestartet werden: " + e.message);
      return null;
    }
  }, [registerGpsJob]);

  // Spinner je Karte ausschalten, sobald ihr Ergebnis da ist
  useEffect(() => {
    if (!analyseRunning) setAnalysingPaths(new Set());
  }, [analyseRunning]);

  /* ═══════════════════════════════════════════════════════════
     Dateien laden — Welle 77: EIN Aufruf liefert Liste + gecachte Analysen;
     nur die Bilder ohne Ergebnis laufen in einen Hintergrund-Job.
     ═══════════════════════════════════════════════════════════ */
  const loadDateien = useCallback(async () => {
    setPhase("loading");
    try {
      let files;
      let ordnerResult;
      if (nurDateien && nurDateien.length > 0) {
        // Spezifische Dateien laden (aus Komprimierung-Seite)
        const data = await api("/fotos/analyse-cache-multi", {
          method: "POST",
          body: JSON.stringify({ dateien: nurDateien }),
        });
        files = data.dateien || [];
        ordnerResult = data.ordner || sourceFolder;
      } else {
        const qs = sourceFolder ? "?ordner=" + encodeURIComponent(sourceFolder) : "";
        const data = await api("/fotos/analyse-cache" + qs);
        files = data.dateien || [];
        ordnerResult = data.ordner || "";
      }
      setInbox(ordnerResult);
      setDateien(files.map(({ analyse, fertig, ...rest }) => rest));
      const settings = {};
      for (const d of files) {
        settings[d.path] = {
          prefix: "", foto_datum: "", annotation: "",
          ziel_ordner: processMode === "umbenennen" ? d.path.substring(0, d.path.lastIndexOf("/")) : "",
          confidence: "none", reason: "", kopie_nok: false, nok_original: false,
          trello_enabled: false, trello_mode: "", trello_grund: "",
          _geodaten: null, orig_name_edit: "", ziel_ist_neu: false,
        };
      }
      setFileSettings(settings);
      setLockedGroups({});
      lockedGroupsRef.current = {};
      // Gecachte Analysen sofort in die Karten (kein Download, kein Warten)
      let fertigCount = 0;
      for (const d of files) {
        if (d.fertig && d.analyse) { uebernehmeAnalyse(d.path, d.analyse); fertigCount++; }
      }
      setSelectedIdx(files.length > 0 ? 0 : -1);
      setPhase("ready");
      const offen = files.filter((d) => !d.fertig).map((d) => d.path);
      setStatusMsg(`${files.length} Bilder geladen, ${fertigCount} aus Cache${offen.length ? `, ${offen.length} werden analysiert…` : ""}`);
      if (offen.length > 0 && !location.state?.autoAnalyse) startGpsJob(offen, false);
    } catch (e) {
      setStatusMsg("Fehler beim Laden: " + e.message);
      setPhase("ready");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFolder, nurDateien, processMode, uebernehmeAnalyse, startGpsJob]);

  useEffect(() => { loadDateien(); }, [loadDateien]);

  /* ═══════════════════════════════════════════════════════════
     Einzel-Analyse
     ═══════════════════════════════════════════════════════════ */
  const analyseEinzel = useCallback(async (pfad, force = false) => {
    setAnalysingPaths((prev) => new Set([...prev, pfad]));
    try {
      const qs = "pfad=" + encodeURIComponent(pfad) + (force ? "&force=true" : "");
      const r = await fetch(
        API_BASE + "/zuordnen/api/fotos/analyse-einzel?" + qs,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      ).then(async (res) => {
        if (!res.ok) {
          let d = ""; try { const j = await res.json(); d = j.detail || ""; } catch {}
          throw new Error("API " + res.status + (d ? ": " + d : ""));
        }
        return res.json();
      });

      uebernehmeAnalyse(pfad, r);
    } catch (e) {
      setStatusMsg("Analyse-Fehler: " + e.message);
    } finally {
      setAnalysingPaths((prev) => { const n = new Set(prev); n.delete(pfad); return n; });
    }
  }, [uebernehmeAnalyse]);

  /* Auto-Analyse nach dem Laden, wenn von KomprimierungPage gekommen */
  const autoAnalyseDone = useRef(false);
  useEffect(() => {
    if (
      phase === "ready" &&
      dateien.length > 0 &&
      location.state?.autoAnalyse &&
      !autoAnalyseDone.current
    ) {
      autoAnalyseDone.current = true;
      startGpsJob(dateien.map((d) => d.path), true);
    }
  }, [phase, dateien, location.state, startGpsJob]);

  /* ═══════════════════════════════════════════════════════════
     Thumbnail laden
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    // Großes Bild lädt NUR nach Klick auf ein Thumbnail (previewPath),
    // nicht mehr automatisch beim Selektieren einer Karte.
    if (!previewPath) {
      setThumbnail(null);
      return;
    }
    const d = dateien.find((x) => x.path === previewPath);
    if (!d) {
      setThumbnail(null);
      return;
    }
    setImgRotation(0);
    setImgZoom(1);
    setImgPan({ x: 0, y: 0 });
    if (thumbCache.current[d.path]) {
      setThumbnail(thumbCache.current[d.path]);
      return;
    }
    setThumbLoading(true);
    // Sofort das kleine Row-Thumbnail als Platzhalter zeigen (kein leeres Panel)
    const small = rowThumbsRef.current[d.path];
    setThumbnail(small && small !== "failed" ? small : null);
    const hashQs = d.content_hash ? "&content_hash=" + encodeURIComponent(d.content_hash) : "";
    api("/fotos/thumbnail?path=" + encodeURIComponent(d.path) + "&max_size=1600" + hashQs)
      .then((r) => {
        thumbCache.current[d.path] = r.image;
        setThumbnail(r.image);
        setThumbLoading(false);
      })
      .catch(() => { setThumbLoading(false); });
  }, [previewPath, dateien]);

  /* ═══════════════════════════════════════════════════════════
     Welle 25: kleine Thumbnails für alle Karten (25er-Batches,
     2 parallel, sichtbare Karten zuerst)
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    thumbObserverRef.current = new IntersectionObserver((entries) => {
      for (const en of entries) {
        const p = en.target.dataset.path;
        if (!p) continue;
        if (en.isIntersecting) visiblePathsRef.current.add(p);
        else visiblePathsRef.current.delete(p);
      }
    }, { rootMargin: "300px" });
    return () => thumbObserverRef.current?.disconnect();
  }, []);

  useEffect(() => {
    rowThumbsRef.current = {};
    setRowThumbs({});
    if (dateien.length === 0) return undefined;
    let cancelled = false;
    const hashes = {};
    dateien.forEach((d) => { if (d.content_hash) hashes[d.path] = d.content_hash; });
    const allPaths = dateien.map((d) => d.path);

    const worker = async () => {
      while (!cancelled) {
        const pending = allPaths.filter((p) => rowThumbsRef.current[p] === undefined);
        if (pending.length === 0) return;
        const visible = pending.filter((p) => visiblePathsRef.current.has(p));
        const batch = (visible.length > 0 ? visible : pending).slice(0, 25);
        batch.forEach((p) => { rowThumbsRef.current[p] = null; }); // in Arbeit
        try {
          const h = {};
          batch.forEach((p) => { if (hashes[p]) h[p] = hashes[p]; });
          const res = await apiPost("/fotos/thumbnails-batch", { paths: batch, size: "w256h256", hashes: h });
          if (cancelled) return;
          const upd = {};
          batch.forEach((p) => { upd[p] = (res.thumbs && res.thumbs[p]) || "failed"; });
          Object.assign(rowThumbsRef.current, upd);
          setRowThumbs((prev) => ({ ...prev, ...upd }));
        } catch {
          if (cancelled) return;
          const upd = {};
          batch.forEach((p) => { upd[p] = "failed"; });
          Object.assign(rowThumbsRef.current, upd);
          setRowThumbs((prev) => ({ ...prev, ...upd }));
        }
      }
    };
    worker();
    worker();
    return () => { cancelled = true; };
  }, [dateien]);

  /* ── Ziel-Ordner Gruppierung: wie viele Dateien pro Ziel ── */
  const zielCounts = useMemo(() => {
    const counts = {};
    for (const [, s] of Object.entries(fileSettings)) {
      const z = (s.ziel_ordner || "").toLowerCase();
      if (z) counts[z] = (counts[z] || 0) + 1;
    }
    return counts;
  }, [fileSettings]);

  /* ═══════════════════════════════════════════════════════════
     Batch-Analyse
     ═══════════════════════════════════════════════════════════ */
  const runAnalyse = useCallback(async () => {
    // Welle 77: Job über alle Bilder (force) — Karten füllen sich Bild für Bild
    await startGpsJob(dateien.map((d) => d.path), true);
  }, [dateien, startGpsJob]);

  /* ═══════════════════════════════════════════════════════════
     Browse
     ═══════════════════════════════════════════════════════════ */
  const browseFolder = useCallback(async (path) => {
    setBrowseLoading(true);
    try {
      const data = await api("/fotos/browse-ordner?path=" + encodeURIComponent(path));
      setBrowseItems(data.ordner || []);
      setBrowsePath(path);
    } catch (e) { console.warn(e); }
    finally { setBrowseLoading(false); }
  }, []);

  const openBrowse = useCallback((dateiPath) => {
    const s = fileSettings[dateiPath] || {};
    const startPath = s.ziel_ordner || "/Holthey Immobilien/Bilder";
    browseTargetPath.current = dateiPath;
    setBrowseOpen(true);
    setBrowsePath(startPath);
    setNewFolderMode(false);
    setNewFolderName("");
    browseFolder(startPath);
  }, [fileSettings, browseFolder]);

  const pickFolder = useCallback((folderPath) => {
    const path = browseTargetPath.current;
    if (path === "__source__") {
      setSourceFolder(folderPath);
    } else if (path) {
      updateZiel(path, folderPath);
    }
    setBrowseOpen(false);
  }, [updateZiel]);

  const createFolder = useCallback(async (parentPath, name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const pfad = parentPath.replace(/\/$/, "") + "/" + trimmed;
    try {
      await apiPost("/bilder/ordner-erstellen", { pfad });
      await browseFolder(parentPath);
    } catch (e) {
      alert("Fehler beim Erstellen: " + e.message);
    }
  }, [browseFolder]);

  /* ═══════════════════════════════════════════════════════════
     Trello
     ═══════════════════════════════════════════════════════════ */
  const openTrelloPopup = useCallback((filePath) => {
    setTrelloPopupPath(filePath);
    setTrelloGrund("");
    const filename = filePath.split("/").pop() || "";
    const datei = dateien.find(x => x.path === filePath);
    const s = fileSettings[filePath] || {};
    const ext = "." + (datei?.name || filename).split(".").pop().toLowerCase();
    // Trello-Titel auf den NEUEN Dateinamen aufbauen (enthält Einheit,
    // Raum, Annotation) und nur Datum + Endung entfernen. Bindestriche
    // bleiben erhalten — keine pauschale `006 04 Bad`-Konvertierung.
    const newFilename = _buildFilename(
      s.prefix, s.foto_datum, s.orig_name_edit || datei?.name || filename,
      s.annotation, ext, s.einheit, s.raum, s.erstellt_am,
    );
    setTrelloTitel(_titleFromFilename(newFilename));
    setTrelloError(""); setTrelloSuccess("");
    setTrelloPopupOpen(true);
  }, [dateien, fileSettings]);

  useEffect(() => {
    if (trelloPopupOpen) setTimeout(() => trelloBeimVerschiebenRef.current?.focus(), 60);
  }, [trelloPopupOpen]);

  const trelloCreateCard = useCallback(async (filePath, mode, grund, zielPfad, prefix, annotation, titel, einheit, raum) => {
    setTrelloSaving(true); setTrelloError(""); setTrelloSuccess("");
    try {
      const resp = await fetch(API_BASE + "/zuordnen/api/trello/create-card", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quell_pfad: filePath,
          ziel_pfad:  zielPfad || null,
          grund:      grund || "",
          prefix:     prefix || null,
          annotation: annotation || null,
          titel:      titel || null,
          einheit:    einheit || null,
          raum:       raum || null,
        }),
      });
      if (!resp.ok) {
        let d = ""; try { const j = await resp.json(); d = j.detail || ""; } catch {}
        throw new Error("API " + resp.status + (d ? ": " + d : ""));
      }
      const res = await resp.json();
      setTrelloSuccess("Card erstellt: " + res.card_url);
      setFileSettings((prev) => ({
        ...prev,
        [filePath]: { ...prev[filePath], trello_enabled: true, trello_mode: mode, trello_grund: grund || "" },
      }));
      return res;
    } catch (e) { setTrelloError(e.message); return null; }
    finally { setTrelloSaving(false); }
  }, []);

  /* ═══════════════════════════════════════════════════════════
     Verschieben — als Hintergrund-Job (Welle 28)
     Komprimieren + Move + Trello laufen serverseitig; das UI bleibt frei.
     ═══════════════════════════════════════════════════════════ */
  const selectedPathRef = useRef(null); // Fokus-Datei halten, wenn andere Zeilen verschwinden
  useEffect(() => { selectedPathRef.current = dateien[selectedIdx]?.path || null; }, [dateien, selectedIdx]);
  const onJobItemResult = useCallback((item) => {
    setInFlightPaths((prev) => { const n = new Set(prev); n.delete(item.quell_pfad); return n; });
    if (item.ok) {
      setStatusMsg(`Verschoben: ${item.dateiname}`);
      setDateien((prev) => {
        const selPath = selectedPathRef.current;
        const next = prev.filter((d) => d.path !== item.quell_pfad);
        if (selPath && selPath !== item.quell_pfad) {
          const ni = next.findIndex((d) => d.path === selPath);
          if (ni >= 0) { setSelectedIdx(ni); return next; }
        }
        setSelectedIdx((i) => Math.min(i, next.length - 1));
        return next;
      });
      setLockedGroups((prev) => { const n = { ...prev }; delete n[item.quell_pfad]; return n; });
      // NEU-Badge für Dateien mit gleichem Zielordner entfernen (Ordner existiert jetzt)
      const movedZiel = item.ziel_pfad ? item.ziel_pfad.split("/").slice(0, -1).join("/").toLowerCase() : "";
      if (movedZiel) {
        setFileSettings((prev) => {
          const next = { ...prev };
          for (const [k, v] of Object.entries(next)) {
            if (v.ziel_ordner && v.ziel_ordner.toLowerCase() === movedZiel && v.ziel_ist_neu) {
              next[k] = { ...v, ziel_ist_neu: false };
            }
          }
          return next;
        });
      }
    } else if (item.fehler) {
      setStatusMsg("Fehler: " + item.fehler);
    }
  }, []);

  const { jobs, startJob, dismissJob } = useVerschiebenJobs({
    pageKey: "fotos",
    onItemResult: onJobItemResult,
  });

  const buildJobItem = useCallback((datei) => {
    const s0 = getSetting(datei.path);
    // Sicherheitsnetz: "006-01" im Prefix wird auch hier noch aufgeteilt
    // (falls das Feld nie verlassen wurde) — die ersten 3 Stellen zählen.
    const teile = _prefixTeile(s0.prefix);
    const s = teile ? { ...s0, prefix: teile.basis, einheit: teile.einheit } : s0;
    const ext = "." + datei.name.split(".").pop().toLowerCase();
    // Umbenennen-Modus: Datei bleibt in ihrem Ordner — AUSSER der Ziel-Ordner
    // wurde explizit geändert (dann bewusstes Verschieben).
    const quellOrdner = datei.path.substring(0, datei.path.lastIndexOf("/"));
    const zielGeaendert = isUmbenennen && s.ziel_ordner &&
      s.ziel_ordner.replace(/\/+$/, "") !== quellOrdner;
    const item = {
      quell_pfad:   datei.path,
      prefix:       s.prefix      || "",
      foto_datum:   s.foto_datum  || "",
      annotation:   s.annotation  || "",
      einheit:      s.einheit     || "",
      raum:         s.raum        || "",
      // Umbenennen wie Verschieben: voller Name inkl. Original-Name + Anmerkung
      // — exakt das, was die Vorschau in der Karte anzeigt.
      dateiname_neu: _buildFilename(s.prefix, s.foto_datum, s.orig_name_edit || datei.name, s.annotation, ext, s.einheit, s.raum, s.erstellt_am),
      ziel_ordner:  isUmbenennen ? (zielGeaendert ? s.ziel_ordner : "") : (s.ziel_ordner || ""),
      kopie_nok:    isUmbenennen ? false : (s.kopie_nok   || false),
      nok_original: false,
      nur_umbenennen: isUmbenennen && !zielGeaendert,
    };
    if (s.trello_enabled && s.trello_mode === "beim_verschieben") {
      item.trello = {
        grund: s.trello_grund || "",
        titel: s.trello_titel || "",
        prefix: s.prefix || "",
        annotation: s.annotation || "",
        einheit: s.einheit || "",
        raum: s.raum || "",
      };
    }
    // Welle 40: Kopie ins Rechnungs-Dateianhang-Verzeichnis
    if (s.anhang_enabled && s.anhang_nummer) {
      item.anhang = { nummer: s.anhang_nummer };
    }
    return item;
  }, [getSetting, isUmbenennen]);

  const submitJob = useCallback(async (items) => {
    if (!items.length) return;
    try {
      await startJob(items);
      setInFlightPaths((prev) => {
        const n = new Set(prev);
        items.forEach((it) => n.add(it.quell_pfad));
        return n;
      });
    } catch (e) {
      setInFlightPaths((prev) => {
        const n = new Set(prev);
        items.forEach((it) => n.delete(it.quell_pfad));
        return n;
      });
      setMoveResult({ ok: false, msg: `Verschieben konnte nicht gestartet werden: ${e.message}` });
      setStatusMsg("Fehler: " + e.message);
    }
  }, [startJob]);

  /* Neue Ziel-Ordner prüfen: Ordner automatisch anlegen ist nur für
     Datums-Ordner ok (letzte Ebene enthält ein Datum wie "2026-07").
     Jeder andere nicht existierende Ordner (z. B. Tippfehler "Bilder/006-01")
     → Warnung, der User muss bestätigen. Gibt false zurück = abbrechen. */
  const pruefeNeueZielordner = useCallback(async (files) => {
    const zuPruefen = new Set();
    for (const d of files) {
      const s = getSetting(d.path);
      const quellOrdner = d.path.substring(0, d.path.lastIndexOf("/"));
      const ziel = (s.ziel_ordner || "").replace(/\/+$/, "");
      if (!ziel) continue;                       // Backend leitet aus Prefix ab
      if (isUmbenennen && ziel === quellOrdner) continue; // bleibt im Ordner
      zuPruefen.add(ziel);
    }
    if (zuPruefen.size === 0) return true;
    let ordnerInfo = {};
    try {
      const res = await apiPost("/fotos/ordner-exists", { pfade: [...zuPruefen] });
      ordnerInfo = res.ordner || {};
    } catch {
      return true; // Prüfung nicht möglich → nicht blockieren
    }
    const DATUM_RE = /\d{4}[-_. ]\d{2}/;
    const warnungen = [];
    for (const [pfad, info] of Object.entries(ordnerInfo)) {
      if (info.exists) continue;
      const letzterTeil = pfad.split("/").pop() || "";
      const istDatumsOrdner = DATUM_RE.test(letzterTeil) && info.parent_exists;
      if (!istDatumsOrdner) warnungen.push(pfad.replace(/^\/Holthey Immobilien\//i, ""));
    }
    if (warnungen.length === 0) return true;
    return window.confirm(
      "Achtung — diese Ziel-Ordner existieren noch NICHT und sind keine Datums-Ordner:\n\n" +
      warnungen.map((w) => "• " + w).join("\n") +
      "\n\nBitte prüfen (Tippfehler?). Trotzdem fortfahren und die Ordner neu anlegen?"
    );
  }, [getSetting, isUmbenennen]);

  const _doMove = useCallback(async (datei) => {
    setMoveResult(null);
    await submitJob([buildJobItem(datei)]);
  }, [buildJobItem, submitJob]);

  const handleMove = useCallback(async (datei) => {
    const s = getSetting(datei.path);
    if (!s.prefix && !s.annotation) {
      setStatusMsg("Bitte Prefix oder Annotation eingeben!");
      return;
    }

    // Neuer Nicht-Datums-Ordner? → User muss bestätigen
    if (!(await pruefeNeueZielordner([datei]))) return;

    // Duplikat-Check VOR dem Verschieben
    setDuplikatChecking(true);
    try {
      const dup = await apiPost("/fotos/duplikat-check", { quell_pfad: datei.path });
      if (dup && dup.duplikat) {
        setDuplikatChecking(false);
        setDuplikatInfo({
          quell: { name: datei.name, path: datei.path, modified: dup.quell_modified || null },
          existierend: dup.existierende_datei,
          onReplace: async () => {
            // Alte Datei im Ziel löschen, dann verschieben
            try {
              await apiPost("/fotos/loeschen", { pfad: dup.existierende_datei.path });
            } catch (e) {
              setStatusMsg("Alte Datei löschen fehlgeschlagen: " + e.message);
              setDuplikatInfo(null);
              return;
            }
            setDuplikatInfo(null);
            await _doMove(datei);
          },
          onDelete: async () => {
            // Neue Datei (Quelle) löschen
            try {
              await apiPost("/fotos/loeschen", { pfad: datei.path });
              setDateien((prev) => {
                const next = prev.filter((d) => d.path !== datei.path);
                setSelectedIdx((i) => Math.min(i, next.length - 1));
                return next;
              });
              setStatusMsg("Duplikat gelöscht");
            } catch (e) {
              setStatusMsg("Löschen fehlgeschlagen: " + e.message);
            }
            setDuplikatInfo(null);
          },
          onKeepBoth: async () => {
            // Beide behalten → normal verschieben (autorename greift in Dropbox)
            setDuplikatInfo(null);
            await _doMove(datei);
          },
          onAbort: () => setDuplikatInfo(null),
        });
        return;
      }
    } catch (e) {
      console.warn("Duplikat-Check fehlgeschlagen:", e);
    }
    setDuplikatChecking(false);

    await _doMove(datei);
  }, [getSetting, _doMove, pruefeNeueZielordner]);

  /* ═══════════════════════════════════════════════════════════
     Löschen
     ═══════════════════════════════════════════════════════════ */
  const handleNachZaehler = useCallback(async (datei) => {
    setNachZaehlerPath(datei.path);
    try {
      const moveRes = await apiPost("/fotos/nach-zaehler", { quell_pfad: datei.path });
      // Welle 12: Wenn die Trello-Checkbox aktiv ist, nach erfolgreichem
      // Verschieben automatisch eine Card erstellen — mit dem neuen
      // Zähler-Inbox-Pfad als Ziel und gleichen Settings wie beim
      // regulären Verarbeitungs-Run.
      const s = fileSettings[datei.path] || {};
      if (s.trello_enabled) {
        try {
          const newPath = moveRes?.ziel_pfad || null;
          const trelloRes = await trelloCreateCard(
            datei.path,
            s.trello_mode || "nach_zaehler",
            s.trello_grund || "",
            newPath,
            s.prefix,
            s.annotation,
            s.trello_titel || null,
            s.einheit,
            s.raum,
          );
          if (trelloRes?.card_id) {
            try { await apiPost("/fotos/update-trello", { foto_name: datei.name, card_id: trelloRes.card_id, card_url: trelloRes.card_url || "" }); } catch {}
          }
        } catch (te) {
          // Trello-Fehler nicht hart machen — Datei ist schon verschoben.
          console.warn("[trello] auto-card nach Zähler-Move fehlgeschlagen:", te);
        }
      }
      setDateien((prev) => {
        const next = prev.filter((d) => d.path !== datei.path);
        setSelectedIdx((i) => Math.min(i, next.length - 1));
        return next;
      });
      setStatusMsg("→ Zähler-Inbox: " + datei.name);
    } catch (e) {
      setStatusMsg("Fehler: " + e.message);
    } finally {
      setNachZaehlerPath(null);
    }
  }, [fileSettings, trelloCreateCard]);

  const handleNachManuell = useCallback(async (datei) => {
    setNachManuellPath(datei.path);
    try {
      await apiPost("/fotos/nach-manuell", { quell_pfad: datei.path });
      setDateien((prev) => {
        const next = prev.filter((d) => d.path !== datei.path);
        setSelectedIdx((i) => Math.min(i, next.length - 1));
        return next;
      });
      setStatusMsg("→ Manuell: " + datei.name);
    } catch (e) {
      setStatusMsg("Fehler: " + e.message);
    } finally {
      setNachManuellPath(null);
    }
  }, []);

  const handleDelete = useCallback(async (datei) => {
    if (!window.confirm(`"${datei.name}" wirklich löschen?`)) return;
    try {
      await apiPost("/fotos/loeschen", { pfad: datei.path });
      setDateien((prev) => {
        const next = prev.filter((d) => d.path !== datei.path);
        setSelectedIdx((i) => Math.min(i, next.length - 1));
        return next;
      });
      setStatusMsg(`Gelöscht: ${datei.name}`);
    } catch (e) {
      setStatusMsg("Fehler: " + e.message);
    }
  }, []);

  /* ═══════════════════════════════════════════════════════════
     Batch-Verschieben
     ═══════════════════════════════════════════════════════════ */
  /* Batch-Dialog öffnen (Kandidaten berechnen, Popup zeigen) */
  const handleBatch = useCallback(() => {
    const candidates = checkedPaths.size > 0
      ? dateien.filter((d) => checkedPaths.has(d.path) && getSetting(d.path).prefix)
      : dateien.filter((d) => {
          const s = getSetting(d.path);
          return (s.confidence === "green" || s.confidence === "yellow") && s.prefix;
        });
    if (!candidates.length) { setStatusMsg("Keine Dateien mit Prefix vorhanden"); return; }
    setBatchCandidates(candidates);
    setBatchResult(null);
    setShowBatch(true);
  }, [dateien, checkedPaths, getSetting]);

  /* Batch: Duplikat-Checks interaktiv VOR dem Abschicken, danach EIN
     Hintergrund-Job für alle — das Modal schließt sofort, der Indikator
     unten rechts zeigt den Fortschritt. */
  const runBatch = useCallback(async () => {
    setShowBatch(false);   // Popup sofort zu — Duplikat-Fragen kommen einzeln, Rest läuft im Hintergrund
    // Neue Nicht-Datums-Ordner? → erst bestätigen lassen, sonst Abbruch
    if (!(await pruefeNeueZielordner(batchCandidates))) return;
    setBatchMoving(true);
    setBatchResult(null);
    setBatchProgress({});
    // Sofort sichtbar: Zeilen ausgrauen + Status in der Ecke
    setInFlightPaths((prev) => {
      const n = new Set(prev);
      batchCandidates.forEach((d) => n.add(d.path));
      return n;
    });
    setJobVorlauf("Duplikat-Prüfung läuft…");
    const geloeschtePaths = new Set();
    const zuVerschieben = [];
    let abgebrochen = false;

    for (const datei of batchCandidates) {
      try {
        const dup = await apiPost("/fotos/duplikat-check", { quell_pfad: datei.path });
        if (dup && dup.duplikat) {
          // Duplikat-Popup zeigen und auf Entscheidung warten
          let dupResult = "abort";
          await new Promise((resolve) => {
            setDuplikatInfo({
              quell: { name: datei.name, path: datei.path, size: dup.quell_size || 0, modified: dup.quell_modified || null },
              existierend: dup.existierende_datei,
              onReplace: async () => {
                try { await apiPost("/fotos/loeschen", { pfad: dup.existierende_datei.path }); } catch {}
                dupResult = "replace";
                setDuplikatInfo(null);
                resolve();
              },
              onDelete: async () => {
                try { await apiPost("/fotos/loeschen", { pfad: datei.path }); } catch {}
                dupResult = "delete";
                setDuplikatInfo(null);
                resolve();
              },
              onKeepBoth: () => { dupResult = "keepboth"; setDuplikatInfo(null); resolve(); },
              onAbort: () => { dupResult = "abort"; setDuplikatInfo(null); resolve(); },
            });
          });
          if (dupResult === "abort") { abgebrochen = true; break; }
          if (dupResult === "delete") { geloeschtePaths.add(datei.path); continue; }
          // replace oder keepboth → normal verschieben
        }
        zuVerschieben.push(datei);
      } catch {
        // Duplikat-Check fehlgeschlagen → Datei trotzdem verschieben (Backend nutzt autorename)
        zuVerschieben.push(datei);
      }
    }

    // Gelöschte Duplikate sofort aus der Liste nehmen
    if (geloeschtePaths.size > 0) {
      setDateien(prev => prev.filter(d => !geloeschtePaths.has(d.path)));
      setFileSettings(prev => {
        const next = { ...prev };
        for (const p of geloeschtePaths) delete next[p];
        return next;
      });
    }

    // Nicht abgeschickte Kandidaten wieder aktiv machen (Duplikat gelöscht /
    // abgebrochen) — die abgeschickten bleiben ausgegraut bis zum Job-Ergebnis
    const submitted = new Set(zuVerschieben.map((d) => d.path));
    setInFlightPaths((prev) => {
      const n = new Set(prev);
      batchCandidates.forEach((d) => { if (!submitted.has(d.path) || abgebrochen) n.delete(d.path); });
      return n;
    });
    if (!abgebrochen && zuVerschieben.length > 0) {
      await submitJob(zuVerschieben.map((d) => buildJobItem(d)));
      setStatusMsg(`${zuVerschieben.length} Datei(en) werden im Hintergrund verschoben`);
    }
    setJobVorlauf("");
    setSelectedIdx(0);
    setCheckedPaths(new Set());
    setBatchMoving(false);
    setShowBatch(false);
  }, [batchCandidates, getSetting, buildJobItem, submitJob, pruefeNeueZielordner]);

  /* ═══════════════════════════════════════════════════════════
     Geodaten Config – Modal öffnen / Eintrag speichern
     ═══════════════════════════════════════════════════════════ */
  const openGeoConfigModal = useCallback((dateiPath) => {
    const s = getSetting(dateiPath);
    const geo = s._geodaten;
    if (!geo?.lat) { setStatusMsg("Keine GPS-Daten vorhanden"); return; }
    setGeoConfigModal({ dateiPath, lat: geo.lat, lon: geo.lon, adresse: geo.adresse || "" });
  }, [getSetting]);

  const handleGeoConfigCreate = useCallback(async (formData) => {
    const { dateiPath, ...data } = formData;
    try {
      await apiPost("/bilder/geodaten-config", {
        lat:      parseFloat(data.lat),
        lon:      parseFloat(data.lon),
        radius_m: parseInt(data.radius_m, 10) || 200,
        adresse:  data.adresse || "",
        objekt:   data.objekt  || "",
        prefix:   data.prefix  || "",
        pfad:     data.pfad    || "",
        bemerkung: data.bemerkung || "",
      });
      setGeoConfigModal(null);
      setStatusMsg("GPS-Eintrag gespeichert – analysiere neu...");
      // Alle Dateien ohne Match neu analysieren
      const toReanalyse = dateien.filter((d) => {
        const s = getSetting(d.path);
        return s._geodaten?.lat && !s._geodaten?.geo_config_id;
      });
      for (const d of toReanalyse) {
        await analyseEinzel(d.path, true);
      }
      setStatusMsg("Fertig");
    } catch (e) {
      setStatusMsg("Fehler: " + e.message);
    }
  }, [dateien, getSetting, analyseEinzel]);

  /* ═══════════════════════════════════════════════════════════
     Gefilterte Dateiliste
     ═══════════════════════════════════════════════════════════ */
  const filteredDateien = useMemo(() => dateien.filter((d) => {
    const s = fileSettings[d.path] || {};
    if (filterPrefix && !(s.prefix || "").toLowerCase().includes(filterPrefix.toLowerCase())) return false;
    if (filterDatum && !(s.foto_datum || "").includes(filterDatum)) return false;
    if (filterText) {
      const haystack = [d.name, s.ziel_ordner, s.reason, s.prefix, s.annotation].filter(Boolean).join(" ").toLowerCase();
      const terms = filterText.toLowerCase().split(/\s+/).filter(Boolean);
      if (!terms.every((t) => haystack.includes(t))) return false;
    }
    return true;
  }), [dateien, fileSettings, filterPrefix, filterDatum, filterText]);

  /* ═══════════════════════════════════════════════════════════
     Welle 25: Auswahl (Shift-Bereich) + Anwenden-Leiste
     ═══════════════════════════════════════════════════════════ */
  /* Sichtbare Pfade in Anzeige-Reihenfolge (Gruppen wie im Render) */
  const getVisualOrderPaths = useCallback(() => {
    const grpd = {};
    filteredDateien.forEach((d) => {
      const sett = fileSettings[d.path] || {};
      const conf = lockedGroups[d.path] || sett.confidence || "none";
      const eff = (conf === "green" && !sett.prefix) ? "yellow" : conf;
      if (!grpd[eff]) grpd[eff] = [];
      grpd[eff].push(d.path);
    });
    const order = [];
    GROUP_ORDER.forEach(({ key }) => { (grpd[key] || []).forEach((p) => order.push(p)); });
    return order;
  }, [filteredDateien, fileSettings, lockedGroups]);

  /* Live-Übertragen: Tippen im Panel wirkt SOFORT auf alle markierten Bilder */
  const applyBarRef = useRef(applyBar);
  useEffect(() => { applyBarRef.current = applyBar; }, [applyBar]);
  const applyPrefixLookupTimer = useRef(null);
  const applyPrefixQueryRef = useRef("");   // Welle 63: letzte Prefix-Abfrage — gegen veraltete Antworten
  const applyZielAutoRef = useRef(false);   // Welle 63: wurde ziel_ordner automatisch gesetzt? (dann überschreibbar)
  const applyZielBasisRef = useRef("");     // Welle 84: Basis (…/#Datum) des letzten Auto-Lookups — nur Anzeige
  const fileSettingsRefFotos = useRef({});
  useEffect(() => { fileSettingsRefFotos.current = fileSettings; }, [fileSettings]);

  /* Welle 84: Ziel-Ordner je Datei nachschlagen — gleicher Prefix, Datums-Endordner aus dem
     Datum JEDER Datei. Schreibt nur in Dateien, deren Ziel leer ist oder aus der Automatik kam. */
  const lookupZielFuerPfade = useCallback(async (paths, prefixWert) => {
    const trimmed = _prefixBasis(prefixWert);
    if (!trimmed || !/^(?:\d{3}|\d{2}[A-Za-z])$/.test(trimmed) || paths.length === 0) return;
    const myQuery = trimmed + "|" + paths.length;
    applyPrefixQueryRef.current = myQuery;
    const fs = fileSettingsRefFotos.current;
    const items = paths.map((p) => ({ path: p, prefix: trimmed, foto_datum: (fs[p] || {}).foto_datum || "" }));
    try {
      const res = await api("/fotos/prefix-lookup-batch", { method: "POST", body: JSON.stringify({ items }) });
      if (applyPrefixQueryRef.current !== myQuery) return; // veraltete Antwort verwerfen
      const erg = res.ergebnisse || {};
      applyZielAutoRef.current = true;
      if (res.basis) applyZielBasisRef.current = res.basis;
      setFileSettings((prev) => {
        const next = { ...prev };
        for (const p of paths) {
          const e = erg[p];
          if (!e || !e.ziel_ordner) continue;
          const cur = next[p] || {};
          const curVal = (cur.ziel_ordner || "").toString();
          if (curVal && cur._ziel_auto === false) continue;   // vom User gesetzt → nicht anfassen
          next[p] = { ...cur, ziel_ordner: e.ziel_ordner, ziel_ist_neu: !!e.ziel_ist_neu, _ziel_auto: true };
        }
        return next;
      });
      // Panel zeigt nur die Basis + Hinweis, nie einen konkreten Datums-Endordner
      setApplyBar((prev) => ({ ...prev, ziel_ordner: res.basis ? `${res.basis}/${trimmed} - <je Datei>` : prev.ziel_ordner }));
    } catch { /* ignore */ }
  }, []);

  /* Panel-Werte auf frisch markierte Bilder anwenden — nur leere Felder */
  const applyBarToPaths = useCallback((paths) => {
    const bar = applyBarRef.current;
    // Welle 84: Auto-Ziel (aus Prefix) wird nicht kopiert, sondern je Datei mit deren Datum nachgeschlagen
    const felder = applyZielAutoRef.current
      ? ["prefix", "einheit", "raum", "annotation"]
      : ["prefix", "einheit", "raum", "annotation", "ziel_ordner"];
    const aktiv = felder.filter((k) => (bar[k] || "").trim());
    if (applyZielAutoRef.current && (bar.prefix || "").trim() && paths.length) {
      setTimeout(() => lookupZielFuerPfade(paths, bar.prefix), 0);
    }
    if (aktiv.length === 0 || paths.length === 0) return;
    setFileSettings((prev) => {
      const next = { ...prev };
      for (const p of paths) {
        const cur = next[p] || {};
        let upd = null;
        for (const k of aktiv) {
          if ((cur[k] ?? "").toString()) continue; // schon gefüllt → nicht anfassen
          if (!upd) upd = { ...cur };
          upd[k] = bar[k];
          if (k === "ziel_ordner") upd.ziel_ist_neu = false;
        }
        if (upd) next[p] = upd;
      }
      return next;
    });
  }, [lookupZielFuerPfade]);

  const handleCheckClick = useCallback((path, shiftKey) => {
    setCheckedPaths((prev) => {
      const n = new Set(prev);
      if (shiftKey && lastCheckedRef.current && lastCheckedRef.current !== path) {
        const order = getVisualOrderPaths();
        const a = order.indexOf(lastCheckedRef.current);
        const b = order.indexOf(path);
        if (a >= 0 && b >= 0) {
          const [from, to] = a < b ? [a, b] : [b, a];
          const target = !n.has(path);
          for (let i = from; i <= to; i++) {
            if (target) n.add(order[i]); else n.delete(order[i]);
          }
          lastCheckedRef.current = path;
          const added = [...n].filter((p) => !prev.has(p));
          if (added.length) setTimeout(() => applyBarToPaths(added), 0);
          return n;
        }
      }
      if (n.has(path)) n.delete(path); else n.add(path);
      lastCheckedRef.current = path;
      // Frisch markiert → bereits eingetippte Panel-Werte sofort übernehmen
      // (nur in leere Felder — vorhandene Einzelwerte bleiben)
      if (n.has(path) && !prev.has(path)) setTimeout(() => applyBarToPaths([path]), 0);
      return n;
    });
  }, [getVisualOrderPaths, applyBarToPaths]);

  const applyFieldToSelection = useCallback((key, value, prevValue) => {
    const paths = [...checkedPaths];
    if (paths.length === 0) return;
    if (key === "ziel_ordner" && applyZielAutoRef.current) return;   // Welle 84: Auto-Ziel läuft je Datei über lookupZielFuerPfade
    setFileSettings((prev) => {
      const next = { ...prev };
      for (const p of paths) {
        const cur = next[p] || {};
        const curVal = (cur[key] ?? "").toString();
        // Einzelfelder gehen vor: nur überschreiben, wenn leer, wenn der Wert
        // vom Panel selbst stammt (== letzter Panel-Wert beim Tippen) — ODER
        // wenn er nur von der GPS-Analyse kam (kein manueller Eintrag).
        const vonGps = key === "prefix" ? !!cur._prefix_auto
                     : key === "ziel_ordner" ? !!cur._ziel_auto
                     : false;
        if (curVal && curVal !== (prevValue ?? "") && !vonGps) continue;
        const upd = { ...cur, [key]: value };
        if (key === "prefix") upd._prefix_auto = false;
        if (key === "ziel_ordner") { upd.ziel_ist_neu = false; upd._ziel_auto = false; }
        next[p] = upd;
      }
      return next;
    });
  }, [checkedPaths]);


  const updateApplyField = useCallback((key, value) => {
    if (key === "foto_datum") return; // Datum wird NIE übertragen — bleibt Original je Foto
    // Ziel-Ordner: Root normalisieren — eingefügte Pfade dürfen mit oder ohne
    // /Holthey Immobilien/ kommen, gespeichert wird immer der volle Pfad
    if (key === "ziel_ordner" && value.trim()) {
      const clean = value.trim().replace(/^\/+/, "").replace(/^(?:Holthey Immobilien\/)+/i, "");
      value = "/Holthey Immobilien/" + clean;
      applyZielAutoRef.current = false; // vom User gesetzt → nicht mehr auto-überschreiben
    } else if (key === "ziel_ordner") {
      applyZielAutoRef.current = true;  // Welle 84: Feld geleert → wieder Automatik je Datei
    }
    const prevValue = applyBarRef.current[key];
    setApplyBar((prev) => ({ ...prev, [key]: value }));
    applyFieldToSelection(key, value, prevValue);
    // Prefix mit Einheiten-Suffix ("006-01") → Einheit automatisch mit übertragen;
    // für die Ordner-Suche zählt nur das Basis-Objekt
    if (key === "prefix") {
      const teile = _prefixTeile(value);
      if (teile) {
        applyFieldToSelection("einheit", teile.einheit, applyBarRef.current.einheit);
        setApplyBar((prev) => ({ ...prev, einheit: teile.einheit }));
      }
    }
    // Prefix → Ziel-Ordner nachschlagen (debounced) und mit-übertragen,
    // solange der User keinen eigenen Ziel-Ordner eingetragen hat
    if (key === "prefix") {
      if (applyPrefixLookupTimer.current) clearTimeout(applyPrefixLookupTimer.current);
      const trimmed = _prefixBasis(value);
      // Welle 63: nur VALIDE Objekte nachschlagen (3 Ziffern oder 2 Ziffern+Buchstabe).
      // "0"/"08"/"08408" NICHT — sonst matcht das Backend früher den falschen 0-Ordner.
      const objOk = /^(?:\d{3}|\d{2}[A-Za-z])$/.test(trimmed);
      if (!trimmed || !objOk) return;
      // Welle 84: solange der User keinen eigenen Ziel-Ordner eingetragen hat, wird je markierter
      // Datei mit DEREN Datum nachgeschlagen (Endordner "<prefix> - <foto_datum>" pro Datei)
      const zielLeerOderAuto =
        !(applyBarRef.current.ziel_ordner || "").trim() || applyZielAutoRef.current;
      if (!zielLeerOderAuto) return;
      const pfade = [...checkedPaths];
      applyPrefixLookupTimer.current = setTimeout(() => { lookupZielFuerPfade(pfade, trimmed); }, 350);
    }
  }, [applyFieldToSelection, checkedPaths, lookupZielFuerPfade]);

  /* Datum-Anzeige im Panel: alle (unterschiedlichen) Daten der markierten
     Bilder — nur Anzeige, das Datum wird nie überschrieben */
  const checkedDatumAnzeige = useMemo(() => {
    const paths = checkedPaths.size > 0
      ? [...checkedPaths]
      : (selectedIdx >= 0 && dateien[selectedIdx] ? [dateien[selectedIdx].path] : []);
    const fmt = (d) => {
      const m = (d || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return m ? `${m[3]}.${m[2]}.${m[1]}` : d;
    };
    const dates = [...new Set(paths.map((p) => (fileSettings[p] || {}).foto_datum || "").filter(Boolean))].map(fmt);
    if (dates.length === 0) return "";
    if (dates.length === 1) return dates[0];
    return dates.slice(0, 3).join(", ") + (dates.length > 3 ? ` +${dates.length - 3}` : "");
  }, [checkedPaths, selectedIdx, dateien, fileSettings]);

  /* Leiste mit den Werten des aktiven Bilds füllen */
  const takeFromActive = useCallback(() => {
    if (selectedIdx < 0 || !dateien[selectedIdx]) return;
    const s = fileSettings[dateien[selectedIdx].path] || {};
    const zielManuell = s.ziel_ordner && s._ziel_auto === false;
    applyZielAutoRef.current = !zielManuell;
    setApplyBar({
      prefix: s.prefix || "",
      einheit: s.einheit || "",
      raum: s.raum || "",
      foto_datum: s.foto_datum || "",
      annotation: s.annotation || "",
      // Welle 84: Auto-Ziel des aktiven Bilds enthält dessen Datums-Endordner → nur Hinweis, nicht übertragen
      ziel_ordner: zielManuell ? s.ziel_ordner : (s.ziel_ordner ? _zielBasisHinweis(s.ziel_ordner) : ""),
    });
  }, [selectedIdx, dateien, fileSettings]);

  /* Panel automatisch mit den Werten des aktiven Bilds füllen
     (aktiv = zuletzt angeklicktes Thumbnail) */
  useEffect(() => {
    if (selectedIdx < 0 || !dateien[selectedIdx]) return;
    const s = fileSettings[dateien[selectedIdx].path] || {};
    const zielManuell = s.ziel_ordner && s._ziel_auto === false;
    applyZielAutoRef.current = !zielManuell;
    setApplyBar({
      prefix: s.prefix || "",
      einheit: s.einheit || "",
      raum: s.raum || "",
      foto_datum: s.foto_datum || "",
      annotation: s.annotation || "",
      ziel_ordner: zielManuell ? s.ziel_ordner : (s.ziel_ordner ? _zielBasisHinweis(s.ziel_ordner) : ""),
    });
    // bewusst NUR bei Wechsel des aktiven Bilds — nicht bei jedem Tippen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdx]);

  /* ═══════════════════════════════════════════════════════════
     Keyboard-Navigation
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "SELECT" || e.target.tagName === "TEXTAREA") return;

      // Build visual order from filtered+grouped
      const visualOrder = [];
      const grpd = {};
      filteredDateien.forEach((d) => {
        const conf = lockedGroups[d.path] || (fileSettings[d.path] || {}).confidence || "none";
        if (!grpd[conf]) grpd[conf] = [];
        grpd[conf].push(d);
      });
      GROUP_ORDER.forEach(({ key }) => { (grpd[key] || []).forEach((d) => visualOrder.push(d)); });

      if (e.key === "Enter") {
        e.preventDefault();
        const curFile = selectedIdx >= 0 ? dateien[selectedIdx] : null;
        if (curFile) {
          setCheckedPaths((prev) => {
            const n = new Set(prev);
            if (n.has(curFile.path)) n.delete(curFile.path); else n.add(curFile.path);
            return n;
          });
          // advance to next
          const curVis = visualOrder.findIndex((d) => d.path === curFile.path);
          if (curVis >= 0 && curVis < visualOrder.length - 1) {
            const next = visualOrder[curVis + 1];
            const nextOrig = dateien.indexOf(next);
            if (nextOrig >= 0) setSelectedIdx(nextOrig);
          }
        }
        return;
      }

      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      setSelectedIdx((i) => e.key === "ArrowDown"
        ? Math.min(i + 1, dateien.length - 1)
        : Math.max(i - 1, 0)
      );
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dateien, filteredDateien, fileSettings, lockedGroups, selectedIdx]);

  /* Welle 77: Der 90-s-Auto-Timer (serielle Nachanalyse aller Bilder) ist
     entfallen — fehlende Analysen laufen als Hintergrund-Job (startGpsJob),
     Barcode-Bilder wandern dort ebenfalls in die Zähler-Inbox. */

  /* ═══════════════════════════════════════════════════════════
     Bild-Viewer
     ═══════════════════════════════════════════════════════════ */
  const handleWheel = (e) => {
    e.preventDefault();
    setImgZoom((z) => Math.max(0.5, Math.min(5, z + (e.deltaY < 0 ? 0.2 : -0.2))));
  };
  const handleImgMouseDown = (e) => {
    if (imgZoom <= 1) return;
    imgDragRef.current = { startX: e.clientX - imgPan.x, startY: e.clientY - imgPan.y };
  };
  const handleImgMouseMove = (e) => {
    if (!imgDragRef.current) return;
    setImgPan({ x: e.clientX - imgDragRef.current.startX, y: e.clientY - imgDragRef.current.startY });
  };
  const handleImgMouseUp = () => { imgDragRef.current = null; };

  /* Mausrad-Zoom im schwebenden Bild-Fenster (non-passive wegen preventDefault) */
  useEffect(() => {
    const el = floatImgAreaRef.current;
    if (!el || !previewPath) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      setImgZoom((z) => Math.max(0.25, Math.min(10, z + (e.deltaY < 0 ? 0.2 : -0.2))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [previewPath]);

  /* ═══════════════════════════════════════════════════════════
     Render
     ═══════════════════════════════════════════════════════════ */
  const selectedFile     = selectedIdx >= 0 ? dateien[selectedIdx] : null;
  const selectedSettings = selectedFile ? getSetting(selectedFile.path) : null;
  const previewFile      = previewPath ? dateien.find((x) => x.path === previewPath) : null;
  const previewSettings  = previewFile ? getSetting(previewFile.path) : null;

  // Gruppen bilden aus filteredDateien
  // "green" ohne Prefix → "yellow" (Datei ist nicht bewegbar ohne Prefix)
  const grouped = {};
  filteredDateien.forEach((d) => {
    const origIdx = dateien.indexOf(d);
    const sett = getSetting(d.path);
    const conf = lockedGroups[d.path] || sett.confidence || "none";
    const effectiveConf = (conf === "green" && !sett.prefix) ? "yellow" : conf;
    if (!grouped[effectiveConf]) grouped[effectiveConf] = [];
    grouped[effectiveConf].push({ ...d, origIdx });
  });

  return (
    <div className="verschieben-page bilder-page fotos-page">

      {/* Header */}
      <div className="v-header">
        <button className="v-btn ghost" onClick={() => navigate(location.state?.returnTo || "/")}>← {location.state?.returnTo ? "Zurück" : "Zurück"}</button>
        <h2 className="v-title"><FaCamera style={{ marginRight: 6 }} /> Fotos {isUmbenennen ? "umbenennen" : "verarbeiten"}</h2>
        <span className="v-tag total">{dateien.length} Bilder</span>
        <div className="v-mode-toggle" style={{ display: "inline-flex", gap: 4, marginLeft: 8, background: "#1e2733", padding: 3, borderRadius: 6 }}>
          <button
            className="v-btn small"
            style={{ background: !isUmbenennen ? "#2e7d32" : "transparent", border: "none", color: "#fff" }}
            onClick={() => setProcessMode("verschieben")}
            title="Fotos in Ziel-Ordner verschieben + umbenennen"
          >Verschieben</button>
          <button
            className="v-btn small"
            style={{ background: isUmbenennen ? "#1565c0" : "transparent", border: "none", color: "#fff" }}
            onClick={() => setProcessMode("umbenennen")}
            title="Nur umbenennen — Datei bleibt im aktuellen Ordner"
          >Umbenennen</button>
        </div>
        <div className="v-header-actions">
          <button className="v-btn ghost small" onClick={loadDateien} disabled={analyseRunning}>
            <FaSync /> Neu laden
          </button>
          <button className="v-btn analyse" onClick={runAnalyse} disabled={analyseRunning}
            title="Alle Bilder neu analysieren (Ergebnisse erscheinen Bild für Bild)">
            <FaSearch /> {analyseRunning ? "Läuft..." : "GPS analysieren"}
          </button>
          {analyseRunning && (
            <span className="v-tag" title={gpsFortschritt.current || ""}>
              ⏳ Bild {gpsFortschritt.done}/{gpsFortschritt.total}{gpsFortschritt.current ? `: ${gpsFortschritt.current}` : ""}
            </span>
          )}
          <button
            className="v-btn zeit-check"
            onClick={handleZeitCheck}
            disabled={zeitChecking || selectedIdx < 0 || !selectedSettings?.prefix}
            title="Zeitlich nahe Fotos (±15min) übernehmen Prefix + Pfad"
          >
            🕐 {zeitChecking ? "Prüfe..." : "Zeit prüfen"}
          </button>
          {checkedPaths.size > 0 && (
            <div className="v-header-marked">
              <button className="v-btn primary" style={{ background: "#2e7d32", border: "none" }}
                onClick={handleBatch} disabled={analyseRunning || moving}>
                <FaCheck /> {isUmbenennen ? "Umbenennen" : "Verschieben"} ({checkedPaths.size})
              </button>
              <button className="v-btn" style={{ background: "#e65100", border: "none", color: "#fff" }}
                onClick={async () => {
                  const paths = [...checkedPaths];
                  if (!window.confirm(`${paths.length} Dateien als NOK verschieben?`)) return;
                  // Hintergrund-Job wie jedes Verschieben; Zeilen verschwinden bei ok
                  await submitJob(paths.map((p) => ({ quell_pfad: p, nok_original: true })));
                  setCheckedPaths(new Set());
                  setStatusMsg(`${paths.length} Datei(en) werden als NOK verschoben`);
                }}>NOK ({checkedPaths.size})</button>
              <button className="v-btn" style={{ background: "#1565c0", border: "none", color: "#fff" }}
                onClick={async () => {
                  const paths = [...checkedPaths];
                  if (!window.confirm(`${paths.length} Dateien in Zähler-Inbox verschieben?`)) return;
                  const erfolg = new Set();
                  for (const p of paths) {
                    // Backend erwartet quell_pfad (nicht pfad) — war vorher immer 400
                    try { await apiPost("/fotos/nach-zaehler", { quell_pfad: p }); erfolg.add(p); } catch {}
                  }
                  setCheckedPaths(new Set(paths.filter(p => !erfolg.has(p))));
                  setDateien(prev => prev.filter(d => !erfolg.has(d.path)));
                  setFileSettings(prev => { const n = { ...prev }; for (const p of erfolg) delete n[p]; return n; });
                  setSelectedIdx(0);
                  const fehler = paths.length - erfolg.size;
                  setStatusMsg(`Zähler: ${erfolg.size} verschoben${fehler > 0 ? `, ${fehler} Fehler (bleiben markiert)` : ""}`);
                }}>Zähler ({checkedPaths.size})</button>
              <button className="v-btn" style={{ background: "#c62828", border: "none", color: "#fff" }}
                onClick={async () => {
                  const paths = [...checkedPaths];
                  if (!window.confirm(`${paths.length} Dateien löschen?`)) return;
                  const erfolg = new Set();
                  for (const p of paths) {
                    try { await apiPost("/fotos/loeschen", { pfad: p }); erfolg.add(p); } catch {}
                  }
                  setCheckedPaths(new Set(paths.filter(p => !erfolg.has(p))));
                  setDateien(prev => prev.filter(d => !erfolg.has(d.path)));
                  setFileSettings(prev => { const n = { ...prev }; for (const p of erfolg) delete n[p]; return n; });
                  setSelectedIdx(0);
                  const fehler = paths.length - erfolg.size;
                  setStatusMsg(`Gelöscht: ${erfolg.size}${fehler > 0 ? `, ${fehler} Fehler (bleiben markiert)` : ""}`);
                }}>Löschen ({checkedPaths.size})</button>
            </div>
          )}
        </div>
        {statusMsg && <span style={{ fontSize: "0.78rem", color: "#8ac4ff", marginLeft: 4 }}>{statusMsg}</span>}
        {barcodeMovedCount > 0 && (
          <span style={{
            fontSize: "0.78rem", padding: "2px 8px", borderRadius: 4,
            color: "#fff176", background: "rgba(245,127,23,0.25)", marginLeft: 4,
          }}>
            📷 {barcodeMovedCount} Foto{barcodeMovedCount > 1 ? "s" : ""} mit Barcode → Zähler-Inbox
          </span>
        )}
        {moveResult && (
          <span style={{
            fontSize: "0.78rem",
            padding: "2px 8px",
            borderRadius: 4,
            color: moveResult.ok ? "#81c784" : "#ef9a9a",
            background: moveResult.ok ? "rgba(46,125,50,0.2)" : "rgba(198,40,40,0.2)",
          }}>
            {moveResult.ok ? "✓" : "✗"} {moveResult.msg}
          </span>
        )}
      </div>

      {/* Source Folder Bar */}
      <div className="v-source-bar">
        <FaFolder className="v-source-icon" />
        <span className="v-source-label">Quell-Ordner:</span>
        {[
          { label: "Bilder", path: "/Holthey Immobilien/--- scan - Ablage/##Rename-me Batch/Batch Input/Bilder" },
          { label: "Zähler", path: "/Holthey Immobilien/--- scan - Ablage/##Rename-me Batch/Batch Input/Zähler" },
          { label: "Pipeline", path: "/Holthey Immobilien/--- scan - Ablage/##Rename-me Batch/Batch Input/Zähler/Pipeline" },
        ].map((tab) => (
          <button
            key={tab.path}
            className={"v-source-tab" + ((sourceFolder || "/Holthey Immobilien/--- scan - Ablage/##Rename-me Batch/Batch Input/Bilder") === tab.path ? " active" : "")}
            onClick={() => { setSourceFolder(tab.path); }}
          >{tab.label}</button>
        ))}
        <button
          className="v-source-tab browse"
          title="Anderen Ordner wählen"
          onClick={() => {
            browseTargetPath.current = "__source__";
            setBrowseOpen(true);
            browseFolder(inbox || "/Holthey Immobilien/--- scan - Ablage/##Rename-me Batch/Batch Input");
          }}
        ><FaFolderOpen /></button>
        {inbox && <span className="v-source-path" title={inbox}>{shortPath(inbox)}</span>}
      </div>

      <div className="v-split">

        {/* Karten-Liste (Welle 25: volle Breite, mittig — Vorschau-Spalte entfernt) */}
        <div
          className="v-file-list"
          ref={listRef}
          style={{ flex: "1 1 auto", minWidth: 0 }}
        >
          {phase === "loading" && <div className="v-empty">Lade Dateien...</div>}

          {/* Filter-Bar */}
          <div className="v-filter-bar">
            <FaSearch className="v-filter-icon" />
            <input
              className="v-filter-input"
              style={{ flex: "none", width: 90 }}
              placeholder="Prefix..."
              value={filterPrefix}
              onChange={(e) => setFilterPrefix(e.target.value)}
            />
            <input
              className="v-filter-input"
              style={{ flex: "none", width: 100 }}
              type="date"
              value={filterDatum}
              onChange={(e) => setFilterDatum(e.target.value)}
            />
            <input
              className="v-filter-input"
              placeholder="Freitext (Leerzeichen = UND)..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
            {(filterPrefix || filterDatum || filterText) && (
              <button className="v-btn ghost small" onClick={() => { setFilterPrefix(""); setFilterDatum(""); setFilterText(""); }} title="Filter löschen">
                <FaTimes />
              </button>
            )}
            <span className="v-filter-count">{filteredDateien.length}/{dateien.length}</span>
            <label
              title="Alle sichtbaren Bilder an-/abwählen"
              style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.75rem", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              <input
                type="checkbox"
                checked={filteredDateien.length > 0 && filteredDateien.every((d) => checkedPaths.has(d.path))}
                onChange={(e) => {
                  const check = e.target.checked;
                  setCheckedPaths((prev) => {
                    const n = new Set(prev);
                    const added = [];
                    filteredDateien.forEach((d) => {
                      if (check) {
                        if (!n.has(d.path)) added.push(d.path);
                        n.add(d.path);
                      } else n.delete(d.path);
                    });
                    if (added.length) setTimeout(() => applyBarToPaths(added), 0);
                    return n;
                  });
                }}
              />
              Alle
            </label>
          </div>

          {GROUP_ORDER.map(({ key: gKey, label: gLabel, icon: gIcon }) => {
            const items = grouped[gKey];
            if (!items || items.length === 0) return null;
            const c = CONF[gKey];

            return (
              <div key={gKey} className="v-group">
                <div
                  className="v-group-header"
                  style={{ borderLeft: `4px solid ${c.border}`, color: c.text }}
                >
                  <span className="v-group-icon">{gIcon}</span>
                  <span className="v-group-label">{gLabel}</span>
                  <span className="v-group-count">{items.length}</span>
                </div>

                {items.map((d) => {
                  const s        = getSetting(d.path);
                  const isActive = d.origIdx === selectedIdx;
                  const ext      = "." + d.name.split(".").pop().toLowerCase();
                  const filename = _buildFilename(s.prefix, s.foto_datum, s.orig_name_edit || d.name, s.annotation, ext, s.einheit, s.raum, s.erstellt_am);
                  const isAnalysing = analysingPaths.has(d.path);
                  const geo      = s._geodaten;
                  const hasGPS   = geo?.lat != null;
                  // hasMatch: entweder DB-Config-ID vorhanden ODER Prefix manuell/aus Cache gesetzt
                  const hasMatch = !!geo?.geo_config_id || !!s.prefix;

                  const inFlight = inFlightPaths.has(d.path);
                  return (
                    <div
                      key={d.path}
                      ref={(el) => {
                        if (el) {
                          cardRefs.current[d.path] = el;
                          el.dataset.path = d.path;
                          thumbObserverRef.current?.observe(el);
                        }
                      }}
                      className={`v-card ${isActive ? "active" : ""}`}
                      title={inFlight ? "Wird gerade verschoben…" : undefined}
                      style={{ borderLeftColor: c.border, position: "relative", paddingLeft: 92, ...(inFlight ? { opacity: 0.45, pointerEvents: "none" } : {}) }}
                      onClick={(e) => {
                        // Fokus nur bei Klick auf freie Fläche — Eingabefelder,
                        // Buttons und Checkboxen reagieren sofort ohne Re-Render
                        if (e.target.closest("input, button, select, textarea, a, datalist")) return;
                        setSelectedIdx(d.origIdx);
                      }}
                    >
                      {/* Welle 25: kleines Thumbnail links — Klick öffnet das
                          schwebende Bild-Fenster und macht die Karte aktiv.
                          (Kein onClickCapture mehr auf der Karte: Felder und
                          Checkboxen aller Karten sind direkt bedienbar.) */}
                      <div
                        title={`${d.name} — klicken für große Ansicht`}
                        onClick={() => { setSelectedIdx(d.origIdx); setPreviewPath(d.path); }}
                        style={{
                          position: "absolute", left: 10, top: 10, width: 74, height: 74,
                          borderRadius: 6, overflow: "hidden", flexShrink: 0,
                          background: "rgba(255,255,255,0.06)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "zoom-in",
                        }}
                      >
                        {rowThumbs[d.path] && rowThumbs[d.path] !== "failed" ? (
                          <img
                            src={`data:image/jpeg;base64,${rowThumbs[d.path]}`}
                            alt=""
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            {...dragOutProps(d.path, d.name)}
                            {...contextShareProps(d.path, d.name)}
                          />
                        ) : (
                          <span style={{ fontSize: "0.62rem", color: "#888" }}>
                            {rowThumbs[d.path] === "failed" ? ext.replace(".", "").toUpperCase() : "…"}
                          </span>
                        )}
                      </div>
                      {/* Row 1: Prefix-Input (+ Einheit-Dropdown) + Dateiname + NOK + Trello */}
                      <div className="v-card-row1">
                        <div className="fp-prefix-wrapper" style={{ position: "relative" }}>
                          <div className="v-input-copy-wrap">
                            <input
                              className="fp-input-prefix"
                              placeholder="Prefix"
                              value={s.prefix || ""}
                              title="Prefix (aus GPS-Match oder manuell)"
                              tabIndex={0}
                              style={{ maxWidth: 90 }}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                updatePrefix(d.path, e.target.value);
                                setEinheitenOpen(prev => ({ ...prev, [d.path]: false }));
                              }}
                              onKeyDown={(e) => { if (e.key === "Enter") commitPrefix(d.path); }}
                              onBlur={() => {
                                commitPrefix(d.path);
                                setTimeout(() => setPrefixSuggest(prev => ({ ...prev, [d.path]: [] })), 150);
                              }}
                            />
                            <button tabIndex={-1} className="v-input-copy-btn" title="Prefix kopieren" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(s.prefix || ""); }}><FaCopy /></button>
                          </div>
                          {prefixSuggest[d.path]?.length > 0 && (
                            <div className="fp-einheit-dropdown" onClick={(e) => e.stopPropagation()}>
                              {prefixSuggest[d.path].map(v => (
                                <div key={v.prefix} className="fp-einheit-option"
                                  onMouseDown={(e) => { e.preventDefault(); selectPrefixSuggest(d.path, v); }}
                                  title={v.adresse || ""}
                                >
                                  <strong>{v.prefix}</strong>
                                  {v.adresse ? <span style={{ color: "#888", marginLeft: 6, fontSize: "0.85em" }}>{v.adresse.split(",")[0]}</span> : null}
                                  {v.pfad ? <span style={{ color: "#4caf50", marginLeft: 4, fontSize: "0.8em" }}>✓ Pfad</span> : null}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Welle 12 — Einheit + Raum direkt neben dem Prefix.
                            Beide optional und fließen NUR in den Dateinamen,
                            nicht in den Ziel-Ordner ein. */}
                        <input
                          tabIndex={-1}
                          className="fp-input-prefix"
                          placeholder="Einheit"
                          title="Einheit aus Adressen-DB (z.B. 01, 02) — fließt nur in den Dateinamen, nicht in den Pfad"
                          list={`einheiten-list-${d.path}`}
                          value={s.einheit || ""}
                          style={{ maxWidth: 90 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const objM = (s.prefix || "").match(/^(\d{2,3}[A-Z]?)/i);
                            if (objM && !einheitenList[d.path]) loadEinheiten(d.path, objM[1]);
                          }}
                          onChange={(e) => { e.stopPropagation(); updateSetting(d.path, "einheit", e.target.value); }}
                        />
                        <datalist id={`einheiten-list-${d.path}`}>
                          {(einheitenList[d.path] || []).map(eh => {
                            const kurz = String(eh).split("-").pop();
                            return <option key={eh} value={kurz} />;
                          })}
                        </datalist>
                        <input
                          tabIndex={-1}
                          className="fp-input-prefix"
                          placeholder="Raum"
                          title="Raum (z.B. Bad) — fließt nur in den Dateinamen. Neue Einträge werden in die Stammdaten übernommen."
                          list="zimmer-list-global"
                          value={s.raum || ""}
                          style={{ maxWidth: 110 }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => { e.stopPropagation(); updateSetting(d.path, "raum", e.target.value); }}
                          onBlur={(e) => { if (e.target.value && !zimmerList.includes(e.target.value)) persistNewRaum(e.target.value); }}
                        />
                        <datalist id="zimmer-list-global">
                          {zimmerList.map(z => (
                            <option key={z} value={z} />
                          ))}
                        </datalist>
                        <div className="v-input-copy-wrap">
                          <input
                            tabIndex={-1}
                            className="fp-fname-input"
                            value={s.orig_name_edit || d.name}
                            title={d.name}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => updateSetting(d.path, "orig_name_edit", e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          />
                          <button tabIndex={-1} className="v-input-copy-btn" title="Dateiname kopieren" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(s.orig_name_edit || d.name); }}><FaCopy /></button>
                        </div>
                        <div className="v-card-checks">
                          <label className="v-mini-check v-trello-check" title="Trello-Card erstellen">
                            <input tabIndex={-1} type="checkbox" checked={s.trello_enabled || false}
                              onChange={(e) => { e.stopPropagation(); if (!s.trello_enabled) { openTrelloPopup(d.path); } else { setFileSettings(prev => ({ ...prev, [d.path]: { ...prev[d.path], trello_enabled: false } })); } }} />
                            <span style={{ color: s.trello_enabled ? "#9c27b0" : undefined }}>Trello</span>
                          </label>
                          <label className="v-mini-check" title="Kopie als Rechnungs-/Angebots-Anhang ablegen">
                            <input tabIndex={-1} type="checkbox" checked={s.anhang_enabled || false}
                              onChange={(e) => { e.stopPropagation(); if (!s.anhang_enabled) { openAnhangPopup(d.path); } else { setFileSettings(prev => ({ ...prev, [d.path]: { ...prev[d.path], anhang_enabled: false } })); } }} />
                            <span style={{ color: s.anhang_enabled ? "#0ea5e9" : undefined }}>Anhang</span>
                          </label>
                        </div>
                      </div>

                      {/* Row 2: Datum + Annotation + GPS-Badge */}
                      <div className="v-card-row2">
                        <div className="v-input-copy-wrap" style={{ flex: "none" }}>
                          <input
                            tabIndex={-1}
                            className="fp-input-datum"
                            type="date"
                            value={s.foto_datum || ""}
                            title={s._datum_from_name ? "Datum aus Dateiname (kein EXIF)" : "Foto-Datum (aus EXIF)"}
                            style={s._datum_from_name ? { borderColor: "#42a5f5", color: "#42a5f5" } : {}}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => updateSetting(d.path, "foto_datum", e.target.value)}
                          />
                          <button tabIndex={-1} className="v-input-copy-btn" title="Datum kopieren" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(s.foto_datum || ""); }}><FaCopy /></button>
                        </div>
                        <div className="v-input-copy-wrap">
                          <input
                            className="fp-input-annotation"
                            placeholder="Anmerkung (z.B. Briefkasten)"
                            value={s.annotation || ""}
                            tabIndex={0}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => updateSetting(d.path, "annotation", e.target.value)}
                          />
                          <button tabIndex={-1} className="v-input-copy-btn" title="Anmerkung kopieren" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(s.annotation || ""); }}><FaCopy /></button>
                        </div>
                        {hasGPS && (
                          <span
                            className="fp-gps-badge"
                            style={{ color: c.text, borderColor: c.border }}
                            title={geo.adresse || "GPS vorhanden"}
                          >
                            <FaMapMarkerAlt style={{ marginRight: 3 }} />
                            {hasMatch ? (geo?.adresse?.split(",")[0] || s.prefix || "Match") : "kein Match"}
                          </span>
                        )}
                      </div>

                      {/* Verschieben-Modus: originales Layout (Ziel-Ordner-Row + Buttons-Row + Vorschau-Name-Row) */}
                      {!isUmbenennen && (
                        <>
                          <div className="v-card-row3">
                            <FaArrowRight className="v-arrow-icon" style={{ color: c.text }} />
                            {s.ziel_ist_neu && (
                              <span className="v-ziel-neu-badge" title="Neuer Ordner – wird beim Verschieben erstellt">NEU</span>
                            )}
                            {(() => {
                              const cnt = s.ziel_ordner ? (zielCounts[(s.ziel_ordner || "").toLowerCase()] || 0) : 0;
                              return cnt > 1 ? (
                                <span className="v-ziel-count-badge" title={cnt + " Dateien gehen in diesen Ordner"}>{cnt}</span>
                              ) : null;
                            })()}
                            <div className="v-input-copy-wrap">
                              <input
                                tabIndex={-1}
                                className="v-ziel-input v-ziel-input-full"
                                value={s.ziel_ordner ? shortPath(s.ziel_ordner) : ""}
                                onChange={(e) => {
                                  // Root-Duplikate beim Einfügen entfernen (kopierte
                                  // Pfade enthalten evtl. schon /Holthey Immobilien/)
                                  const clean = e.target.value.replace(/^\/+/, "").replace(/^(?:Holthey Immobilien\/)+/i, "");
                                  updateZiel(d.path, "/Holthey Immobilien/" + clean);
                                }}
                                placeholder="Ziel-Ordner..."
                                title={s.ziel_ordner || ""}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <button tabIndex={-1} className={"v-input-copy-btn" + (copiedPath === d.path ? " copied" : "")} title="Pfad kopieren"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const pfad = s.ziel_ordner ? shortPath(s.ziel_ordner) : "";
                                  if (pfad) {
                                    navigator.clipboard.writeText(pfad);
                                    setCopiedPath(d.path);
                                    setTimeout(() => setCopiedPath(null), 1200);
                                  }
                                }}>
                                {copiedPath === d.path ? <FaCheck /> : <FaCopy />}
                              </button>
                            </div>
                            <button
                              tabIndex={-1}
                              className="v-browse-btn"
                              title="Ziel-Ordner wählen"
                              onClick={(e) => { e.stopPropagation(); openBrowse(d.path); }}
                            >
                              <FaFolderOpen />
                            </button>
                            <button
                              tabIndex={-1}
                              className="v-analyse-einzel-btn"
                              title="Neu analysieren — aus Cache oder GPS neu"
                              disabled={isAnalysing}
                              onClick={(e) => { e.stopPropagation(); setEnginePickerFile({ path: d.path, name: d.name }); }}
                            >
                              <FaSync className={isAnalysing ? "spin" : ""} />
                            </button>
                            <div className="v-move-group">
                              <button
                                tabIndex={0}
                                className="v-move-btn"
                                title={duplikatChecking ? "Prüfe auf Duplikate..." : "Verschieben"}
                                disabled={moving || duplikatChecking}
                                onClick={(e) => { e.stopPropagation(); handleMove(d); }}
                              >
                                {duplikatChecking ? <FaSync className="spin" /> : <FaArrowRight />}
                              </button>
                              <input
                                tabIndex={-1}
                                type="checkbox"
                                className="v-check-batch"
                                title="Für Massenverarbeitung markieren"
                                checked={checkedPaths.has(d.path)}
                                onClick={(e) => { e.stopPropagation(); handleCheckClick(d.path, e.shiftKey); }}
                                onChange={() => {}}
                              />
                            </div>
                            <button
                              tabIndex={-1}
                              className="v-delete-btn"
                              title="Löschen"
                              onClick={(e) => { e.stopPropagation(); handleDelete(d); }}
                            >
                              <FaTrash />
                            </button>
                            <div className="v-row3-separator" />
                            <button
                              tabIndex={-1}
                              className="v-specific-btn clr-cyan"
                              title="In Zähler-Inbox verschieben"
                              disabled={nachZaehlerPath === d.path}
                              onClick={(e) => { e.stopPropagation(); handleNachZaehler(d); }}
                            >
                              {nachZaehlerPath === d.path ? <FaSync className="spin" /> : <FaBolt />}
                            </button>
                            <button
                              tabIndex={-1}
                              className="v-specific-btn"
                              style={{ color: "#ff7043" }}
                              title="In Manuell-Ordner verschieben"
                              disabled={nachManuellPath === d.path}
                              onClick={(e) => { e.stopPropagation(); handleNachManuell(d); }}
                            >
                              {nachManuellPath === d.path ? <FaSync className="spin" /> : <FaFolderPlus />}
                            </button>
                            {hasGPS && !hasMatch && (
                              <button
                                tabIndex={-1}
                                className="v-specific-btn clr-teal"
                                title="Neuen GPS-Eintrag anlegen"
                                onClick={(e) => { e.stopPropagation(); openGeoConfigModal(d.path); }}
                              >
                                <FaMapMarkerAlt />
                              </button>
                            )}
                          </div>
                          <div className="v-card-row3">
                            <span className="v-card-reason" style={{ color: "#888", flex: 1, minWidth: 0 }} title={filename}>
                              → {filename}
                            </span>
                          </div>
                        </>
                      )}

                      {/* Umbenennen-Modus: Ordner-Zeile — vorbefüllt mit dem eigenen
                          Quell-Ordner; nur bei EXPLIZITER Änderung wird verschoben */}
                      {isUmbenennen && (() => {
                        const quellOrdner = d.path.substring(0, d.path.lastIndexOf("/"));
                        const bleibt = !s.ziel_ordner || s.ziel_ordner.replace(/\/+$/, "") === quellOrdner;
                        return (
                          <div className="v-card-row3" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <span
                              className="v-ziel-neu-badge"
                              style={bleibt
                                ? { background: "rgba(46,125,50,0.18)", color: "#66bb6a", flexShrink: 0 }
                                : { background: "rgba(237,108,2,0.18)", color: "#ffa726", flexShrink: 0 }}
                              title={bleibt
                                ? "Datei bleibt in ihrem Ordner (nur Umbenennen + Komprimieren)"
                                : "Ziel-Ordner wurde geändert — Datei wird dorthin verschoben"}
                            >
                              {bleibt ? "bleibt hier" : "wird verschoben"}
                            </span>
                            <input
                              tabIndex={-1}
                              className="v-ziel-input v-ziel-input-full"
                              value={s.ziel_ordner ? shortPath(s.ziel_ordner) : ""}
                              onChange={(e) => {
                                const clean = e.target.value.replace(/^\/+/, "").replace(/^(?:Holthey Immobilien\/)+/i, "");
                                updateZiel(d.path, "/Holthey Immobilien/" + clean);
                              }}
                              placeholder="Ordner (leer = bleibt im Quell-Ordner)"
                              title={s.ziel_ordner || quellOrdner}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        );
                      })()}

                      {/* Umbenennen-Modus: Vorschau-Name links + Buttons rechts in einer Row */}
                      {isUmbenennen && (
                        <div className="v-card-row3" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <span className="v-card-reason" style={{ color: "#888", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={filename}>
                            → {filename}
                          </span>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                            <button
                              tabIndex={-1}
                              className="v-analyse-einzel-btn"
                              title="Neu analysieren — aus Cache oder GPS neu"
                              disabled={isAnalysing}
                              onClick={(e) => { e.stopPropagation(); setEnginePickerFile({ path: d.path, name: d.name }); }}
                            >
                              <FaSync className={isAnalysing ? "spin" : ""} />
                            </button>
                            <div className="v-move-group">
                              <button
                                tabIndex={0}
                                className="v-move-btn"
                                title={duplikatChecking ? "Prüfe auf Duplikate..." : "Umbenennen"}
                                disabled={moving || duplikatChecking}
                                onClick={(e) => { e.stopPropagation(); handleMove(d); }}
                              >
                                {duplikatChecking ? <FaSync className="spin" /> : <FaArrowRight />}
                              </button>
                              <input
                                tabIndex={-1}
                                type="checkbox"
                                className="v-check-batch"
                                title="Für Massenverarbeitung markieren"
                                checked={checkedPaths.has(d.path)}
                                onClick={(e) => { e.stopPropagation(); handleCheckClick(d.path, e.shiftKey); }}
                                onChange={() => {}}
                              />
                            </div>
                            <button
                              tabIndex={-1}
                              className="v-delete-btn"
                              title="Löschen"
                              onClick={(e) => { e.stopPropagation(); handleDelete(d); }}
                            >
                              <FaTrash />
                            </button>
                            <div className="v-row3-separator" />
                            <button
                              tabIndex={-1}
                              className="v-specific-btn clr-cyan"
                              title="In Zähler-Inbox verschieben"
                              disabled={nachZaehlerPath === d.path}
                              onClick={(e) => { e.stopPropagation(); handleNachZaehler(d); }}
                            >
                              {nachZaehlerPath === d.path ? <FaSync className="spin" /> : <FaBolt />}
                            </button>
                            <button
                              tabIndex={-1}
                              className="v-specific-btn"
                              style={{ color: "#ff7043" }}
                              title="In Manuell-Ordner verschieben"
                              disabled={nachManuellPath === d.path}
                              onClick={(e) => { e.stopPropagation(); handleNachManuell(d); }}
                            >
                              {nachManuellPath === d.path ? <FaSync className="spin" /> : <FaFolderPlus />}
                            </button>
                            {hasGPS && !hasMatch && (
                              <button
                                tabIndex={-1}
                                className="v-specific-btn clr-teal"
                                title="Neuen GPS-Eintrag anlegen"
                                onClick={(e) => { e.stopPropagation(); openGeoConfigModal(d.path); }}
                              >
                                <FaMapMarkerAlt />
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Reason-Bar */}
                      {s.reason && (
                        <div className="v-card-reason" style={{ color: c.text, marginTop: 3 }}>
                          {hasGPS && <FaMapMarkerAlt style={{ marginRight: 4 }} />}
                          {s.reason}
                        </div>
                      )}

                      {/* Trello / Anhang Indikatoren */}
                      {(s.trello_enabled || s.anhang_enabled) && (
                        <div className="fp-card-indicators">
                          {s.trello_enabled && (
                            <span className="fp-ind fp-ind-trello">🟣 {s.trello_mode === "beim_verschieben" ? "beim Verschieben" : "direkt"}</span>
                          )}
                          {s.anhang_enabled && (
                            <span className="fp-ind fp-ind-trello" style={{ color: "#0ea5e9" }}>📎 {s.anhang_nummer}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {phase === "ready" && dateien.length === 0 && (
            <div className="v-empty">Keine Bilder im Inbox-Ordner gefunden.</div>
          )}
        </div>

        {/* Welle 25: rechtes Panel — Werte übertragen, immer sichtbar
            beim Scrollen (Liste scrollt links intern) */}
        <div
          className="v-preview"
          style={{
            flex: "0 0 340px", maxWidth: 340, overflowY: "auto",
            alignSelf: "flex-start",
            margin: "8px 8px 8px 0", padding: "14px 16px",
            background: "rgba(124,106,247,0.08)",
            border: "1px solid rgba(124,106,247,0.35)",
            borderRadius: 10,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: 4 }}>Werte übertragen</div>
          <div style={{ fontSize: "0.73rem", color: "#99a", marginBottom: 10 }}>
            {checkedPaths.size > 0
              ? `${checkedPaths.size} Bild${checkedPaths.size > 1 ? "er" : ""} markiert — jede Eingabe hier wird sofort auf alle markierten übertragen`
              : "Bilder per Checkbox markieren (Shift-Klick = Bereich). Was du dann hier tippst, gilt sofort für alle markierten Bilder."}
          </div>
          {[
            { key: "prefix", label: "Prefix", title: "Ziel-Ordner wird automatisch nachgeschlagen" },
            { key: "einheit", label: "Einheit", title: "Fließt nur in den Dateinamen" },
            { key: "raum", label: "Raum", title: "Fließt nur in den Dateinamen", list: "zimmer-list-global" },
            { key: "foto_datum", label: "Datum", readonly: true, title: "Nur Anzeige — jedes Foto behält immer sein Original-Datum, es wird nie überschrieben" },
            { key: "annotation", label: "Anmerkung", title: "z.B. Briefkasten" },
            { key: "ziel_ordner", label: "Ziel-Ordner", title: "Leer lassen: Ordner kommt aus dem Prefix, der Datums-Endordner (<Prefix> - <Datum>) wird je Foto aus dessen Datum gebildet. Eigener Pfad hier = 1:1 auf alle markierten." },
          ].map((f) => (
            <label key={f.key} style={{ display: "block", marginBottom: 8 }}>
              <span style={{ display: "block", fontSize: "0.72rem", color: "#8a8aa8", marginBottom: 2 }}>{f.label}</span>
              <input
                type={f.type || "text"}
                title={f.title || ""}
                list={f.list}
                placeholder={f.readonly ? "bleibt original je Foto" : f.label}
                readOnly={f.readonly || undefined}
                tabIndex={f.readonly ? -1 : undefined}
                style={{
                  width: "100%", maxWidth: "none", boxSizing: "border-box",
                  background: f.readonly ? "#10131f" : "#141828",
                  color: f.readonly ? "#7a7f99" : "#e8e8f2",
                  border: "1px solid #3a3f5c", borderRadius: 6,
                  padding: "7px 9px", fontSize: "0.84rem",
                  fontFamily: "inherit",
                  opacity: f.readonly ? 0.75 : 1,
                  cursor: f.readonly ? "not-allowed" : undefined,
                }}
                value={f.key === "foto_datum" ? checkedDatumAnzeige
                  : (f.key === "ziel_ordner" && (applyBar.ziel_ordner || "").includes("<je Datei>")) ? "" : applyBar[f.key]}
                onChange={(e) => updateApplyField(f.key, e.target.value)}
                onBlur={f.key === "raum" ? () => persistNewRaum(applyBar.raum) : undefined}
              />
              {f.key === "ziel_ordner" && (applyBar.ziel_ordner || "").includes("<je Datei>") && (
                <span style={{ display: "block", fontSize: "0.68rem", color: "#7a7f99", marginTop: 2 }} title={applyBar.ziel_ordner}>
                  {shortPath(applyBar.ziel_ordner.replace(/<je Datei>$/, ""))}‹Datum je Foto› — eigenen Pfad eintippen, um alle gleich zu setzen.
                </span>
              )}
            </label>
          ))}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
            <button
              className="v-btn primary"
              style={{ background: "#2e7d32", border: "none", justifyContent: "center" }}
              disabled={checkedPaths.size === 0 || analyseRunning || moving || batchMoving}
              title="Die markierten Bilder direkt verschieben (wie der Knopf oben)"
              onClick={handleBatch}
            >{isUmbenennen ? "Umbenennen" : "Verschieben"} ({checkedPaths.size})</button>
            {/* Welle 64 (SIJ-665): Massenaktionen Anhang + Trello */}
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className="v-btn ghost small"
                style={{ flex: 1, justifyContent: "center", color: "#0ea5e9", borderColor: "#0ea5e9" }}
                title="Eine RE-/AN-Nummer eingeben — Anhang-Haken wird bei allen markierten Bildern gesetzt"
                disabled={checkedPaths.size === 0}
                onClick={openAnhangPopupMass}
              >📎 Anhang…</button>
              <button
                className="v-btn ghost small"
                style={{
                  flex: 1, justifyContent: "center",
                  color: alleMarkiertenTrelloAn ? "#fff" : "#ce93d8",
                  borderColor: "#9c27b0",
                  background: alleMarkiertenTrelloAn ? "#9c27b0" : "transparent",
                }}
                title="Trello-Karte automatisch beim Verschieben — für alle markierten Bilder (kein Popup)"
                disabled={checkedPaths.size === 0}
                onClick={toggleTrelloMass}
              >Trello {alleMarkiertenTrelloAn ? "✓" : ""}</button>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className="v-btn ghost small"
                style={{ flex: 1, justifyContent: "center" }}
                title="Felder mit den Werten des aktiven Bildes füllen"
                disabled={selectedIdx < 0}
                onClick={takeFromActive}
              >Vom aktiven Bild</button>
              <button
                className="v-btn ghost small"
                style={{ flex: 1, justifyContent: "center" }}
                title="Alle Markierungen entfernen"
                disabled={checkedPaths.size === 0}
                onClick={() => { setCheckedPaths(new Set()); lastCheckedRef.current = null; }}
              >Auswahl aufheben</button>
            </div>
          </div>
        </div>


        {/* Welle 25: rechte Spalte entfernt — Ordner-Browser schwebt jetzt */}
        {browseOpen && (
                <div className="v-browse-panel" style={{
                  position: "fixed", right: 24, top: 110, width: 400,
                  maxHeight: "72vh", overflowY: "auto", zIndex: 1300,
                  background: "var(--color-surface, #1a1a2e)",
                  border: "1px solid rgba(124,106,247,0.5)", borderRadius: 8,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.6)", padding: 10,
                  transform: `translate(${browseDrag.x}px, ${browseDrag.y}px)`,
                }}>
                  <div className="v-browse-header" onMouseDown={onBrowseDragStart} style={{ cursor: "move" }} title="Zum Verschieben ziehen">
                    <span className="v-browse-title">Ordner wählen</span>
                    <button className="v-dismiss" onClick={() => setBrowseOpen(false)}>×</button>
                  </div>
                  <div className="v-browse-current-path">
                    <FaFolder className="v-browse-current-icon" />
                    {browsePath && browsePath !== "/" && (() => {
                      const parent = browsePath.replace(/\/[^/]+\/?$/, "") || "/";
                      return parent !== browsePath ? (
                        <button className="v-crumb" style={{ marginRight: 4, fontWeight: "bold" }} onClick={() => browseFolder(parent)} title="Übergeordneter Ordner">↑</button>
                      ) : null;
                    })()}
                    {browsePath ? browsePath.split("/").filter(Boolean).reduce((acc, part) => {
                      const p = (acc.length > 0 ? acc[acc.length - 1].p : "") + "/" + part;
                      acc.push({ name: part, p });
                      return acc;
                    }, []).map((seg, i, arr) => (
                      <span key={seg.p} style={{ display: "inline-flex", alignItems: "center" }}>
                        {i > 0 && <span style={{ color: "#444", margin: "0 2px" }}>/</span>}
                        <button
                          className={"v-crumb" + (i === arr.length - 1 ? " active" : "")}
                          onClick={() => browseFolder(seg.p)}
                        >{seg.name}</button>
                      </span>
                    )) : <span style={{ color: "#555" }}>…</span>}
                  </div>
                  {browseLoading ? (
                    <div className="v-browse-loading">Lade...</div>
                  ) : (
                    <div className="v-browse-items">
                      <button className="v-browse-item pick" onClick={() => pickFolder(browsePath)}>
                        <FaCheck /> Hier ablegen
                      </button>
                      {newFolderMode ? (
                        <div className="v-new-folder-row">
                          <FaFolderPlus className="v-new-folder-icon" />
                          <input
                            className="v-new-folder-input"
                            autoFocus
                            placeholder="Ordner-Name..."
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { createFolder(browsePath, newFolderName); setNewFolderMode(false); setNewFolderName(""); }
                              if (e.key === "Escape") { setNewFolderMode(false); setNewFolderName(""); }
                            }}
                          />
                          <button className="v-btn ghost small" title="Erstellen" onClick={() => { createFolder(browsePath, newFolderName); setNewFolderMode(false); setNewFolderName(""); }}><FaCheck /></button>
                          <button className="v-btn ghost small" title="Abbrechen" onClick={() => { setNewFolderMode(false); setNewFolderName(""); }}><FaTimes /></button>
                        </div>
                      ) : (
                        <button className="v-browse-item new-folder" onClick={() => { setNewFolderMode(true); setNewFolderName(""); }}>
                          <FaFolderPlus /> Neuen Ordner erstellen
                        </button>
                      )}
                      {browseItems.map((item) => (
                        <button key={item.path} className="v-browse-item"
                          onClick={() => browseFolder(item.path)}>
                          <FaFolder /> {item.name}
                        </button>
                      ))}
                      {browseItems.length === 0 && <div className="v-browse-empty">Keine Unterordner</div>}
                    </div>
                  )}
                </div>
              )}

        {/* Welle 25: schwebendes Bild-Fenster — am Kopf verschiebbar,
            an der Ecke unten rechts in der Größe veränderbar */}
        {previewFile && (
          <div
            style={{
              position: "fixed", left: floatPos.x, top: floatPos.y, zIndex: 1250,
              width: 600, height: 540, minWidth: 340, minHeight: 300,
              maxWidth: "94vw", maxHeight: "90vh",
              resize: "both", overflow: "hidden",
              display: "flex", flexDirection: "column",
              background: "var(--color-surface, #14142a)",
              border: "1px solid rgba(124,106,247,0.55)", borderRadius: 8,
              boxShadow: "0 14px 44px rgba(0,0,0,0.6)",
            }}
          >
            {/* Kopfzeile: Titel + Werkzeuge + Schließen — hier verschieben */}
            <div
              onMouseDown={startFloatDrag}
              style={{
                display: "flex", alignItems: "center", gap: 4, padding: "6px 10px",
                cursor: "move", userSelect: "none", flexShrink: 0,
                background: "rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <span style={{ flex: 1, fontSize: "0.78rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={previewFile.name}>
                {previewFile.name}
              </span>
              <button className="v-img-tool-btn" title="Links drehen" onClick={() => setImgRotation((r) => r - 90)}><FaUndo /></button>
              <button className="v-img-tool-btn" title="Rechts drehen" onClick={() => setImgRotation((r) => r + 90)}><FaRedo /></button>
              <button className="v-img-tool-btn" title="Verkleinern" onClick={() => { setImgZoom((z) => Math.max(0.25, z / 1.3)); setImgPan({ x: 0, y: 0 }); }}><FaSearchMinus /></button>
              <span className="v-img-zoom-label">{Math.round(imgZoom * 100)}%</span>
              <button className="v-img-tool-btn" title="Vergrößern" onClick={() => setImgZoom((z) => Math.min(10, z * 1.3))}><FaSearchPlus /></button>
              <button className="v-img-tool-btn" title="Zurücksetzen" onClick={() => { setImgZoom(1); setImgRotation(0); setImgPan({ x: 0, y: 0 }); }}><FaCompress /></button>
              <button className="v-img-tool-btn" title="Schließen" style={{ marginLeft: 4 }} onClick={() => setPreviewPath(null)}><FaTimes /></button>
            </div>
            {/* Bildfläche: Mausrad = Zoom, Ziehen = Verschieben (bei Zoom) */}
            <div
              ref={floatImgAreaRef}
              style={{ flex: 1, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: "#0b0b18", minHeight: 0 }}
              onMouseDown={handleImgMouseDown}
              onMouseMove={handleImgMouseMove}
              onMouseUp={handleImgMouseUp}
              onMouseLeave={handleImgMouseUp}
            >
              {thumbLoading && !thumbnail ? (
                <span style={{ color: "#889", fontSize: "0.85rem" }}>Bild laden...</span>
              ) : thumbnail ? (
                <img
                  src={`data:image/jpeg;base64,${thumbnail}`}
                  alt={previewFile.name}
                  draggable={false}
                  style={{
                    maxWidth: "100%", maxHeight: "100%", objectFit: "contain",
                    transform: `rotate(${imgRotation}deg) scale(${imgZoom}) translate(${imgPan.x / imgZoom}px, ${imgPan.y / imgZoom}px)`,
                    transformOrigin: "center center",
                    cursor: imgZoom > 1 ? (imgDragRef.current ? "grabbing" : "grab") : "default",
                    transition: imgDragRef.current ? "none" : "transform 0.15s ease",
                  }}
                />
              ) : (
                <span style={{ color: "#889", fontSize: "0.85rem" }}>Keine Vorschau</span>
              )}
            </div>
            {/* Fußzeile: neuer Name + GPS */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "5px 10px", fontSize: "0.72rem", color: "#9aa", borderTop: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {"→ "}{_buildFilename(
                  previewSettings?.prefix,
                  previewSettings?.foto_datum,
                  previewSettings?.orig_name_edit || previewFile.name,
                  previewSettings?.annotation,
                  "." + previewFile.name.split(".").pop().toLowerCase(),
                  previewSettings?.einheit,
                  previewSettings?.raum,
                )}
              </span>
              {previewSettings?._geodaten?.lat && (
                <a
                  href={`https://www.google.com/maps?q=${previewSettings._geodaten.lat},${previewSettings._geodaten.lon}`}
                  target="_blank" rel="noreferrer" style={{ color: "#8ac4ff", whiteSpace: "nowrap" }}
                >Maps</a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════
          Trello Popup
          ════════════════════════════════════════ */}
      {/* Welle 40: Anhang-Popup — Kopie ins Rechnungs-Dateianhang-Verzeichnis */}
      {anhangPopupOpen && (anhangPopupPath || anhangMassMode) && (
        <div className="v-overlay" onClick={() => setAnhangPopupOpen(false)}>
          <div className="v-trello-modal v-modal" onClick={(e) => e.stopPropagation()}>
            <div className="v-modal-header">
              <h3 style={{ color: "#0ea5e9" }}>📎 Als Rechnungs-/Angebots-Anhang ablegen</h3>
              <button className="v-dismiss" onClick={() => setAnhangPopupOpen(false)}>&#215;</button>
            </div>
            <p style={{ fontSize: "0.85rem", opacity: 0.75, margin: "4px 0 10px" }}>
              Beim Verschieben wird eine Kopie in den Dateianhang-Ordner der Rechnungen
              gelegt. Läuft die Rechnung/das Angebot durch Rename-me, wird sie automatisch angehängt.
            </p>
            <label style={{ display: "block", fontSize: "0.85rem", marginBottom: 4 }}>
              Rechnungs- oder Angebotsnummer
            </label>
            <input
              ref={anhangInputRef}
              type="text"
              value={anhangNummer}
              placeholder="z. B. RE3280 oder AN77"
              onChange={(e) => setAnhangNummer(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") bestaetigeAnhang(); }}
              style={{ width: "100%", boxSizing: "border-box", marginBottom: 8 }}
            />
            {anhangPrefixPreview(anhangNummer) && (
              <p style={{ fontSize: "0.8rem", opacity: 0.7, margin: "0 0 12px" }}>
                Kopie wird abgelegt als:{" "}
                <code>HIG - {anhangPrefixPreview(anhangNummer)} - {new Date().toISOString().slice(0, 10)} - &lt;Dateiname&gt;</code>
              </p>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="v-btn" onClick={() => setAnhangPopupOpen(false)}>Abbrechen</button>
              <button className="v-btn" style={{ background: "#0ea5e9", color: "#fff" }}
                disabled={!anhangPrefixPreview(anhangNummer)} onClick={bestaetigeAnhang}>
                📎 Beim Verschieben kopieren
              </button>
            </div>
          </div>
        </div>
      )}

      {trelloPopupOpen && trelloPopupPath && (() => {
        const tDatei   = dateien.find((d) => d.path === trelloPopupPath);
        const tSett    = fileSettings[trelloPopupPath] || {};
        const tFilename = tDatei?.name || trelloPopupPath.split("/").pop();
        const _ext     = "." + tFilename.split(".").pop().toLowerCase();
        const tNewFilename = _buildFilename(
          tSett.prefix, tSett.foto_datum, tSett.orig_name_edit || tFilename,
          tSett.annotation, _ext, tSett.einheit, tSett.raum, tSett.erstellt_am,
        );
        const tTitleBase = _titleFromFilename(tNewFilename);
        return (
          <div className="v-overlay" onClick={() => { if (!trelloSaving) setTrelloPopupOpen(false); }}>
            <div className="v-trello-modal v-modal" onClick={(e) => e.stopPropagation()}>
              <div className="v-modal-header">
                <h3 style={{ color: "#9c27b0" }}>🟣 Trello-Card erstellen</h3>
                <button className="v-dismiss" onClick={() => setTrelloPopupOpen(false)}>&#215;</button>
              </div>
              <div className="v-trello-body">
                <label className="v-strg-field">
                  <span>Card-Titel</span>
                  <input
                    value={trelloTitel}
                    onChange={e => setTrelloTitel(e.target.value)}
                    placeholder={tFilename}
                  />
                </label>
                <label className="v-strg-field">
                  <span>Dropbox-Pfad</span>
                  <input readOnly value={trelloPopupPath} style={{ opacity: 0.6, fontSize: "0.75rem" }} />
                </label>
                <label className="v-strg-field">
                  <span>Warum / Anmerkung</span>
                  <textarea
                    rows={3}
                    placeholder="z.B. GPS nicht erkannt, manuell prüfen..."
                    value={trelloGrund}
                    onChange={(e) => {
                      const newGrund = e.target.value;
                      setTrelloGrund(newGrund);
                      // Wenn der User den Titel noch nicht manuell verändert
                      // hat (er entspricht dem Auto-Titel mit alten oder
                      // ohne Grund-Keywords), aktualisieren wir ihn mit den
                      // frischen Keywords aus newGrund.
                      const withOldKw = trelloGrund ? `${tTitleBase} – ${extractGrundKeywords(trelloGrund)}` : tTitleBase;
                      if (trelloTitel === tTitleBase || trelloTitel === withOldKw) {
                        const kw = extractGrundKeywords(newGrund);
                        setTrelloTitel(kw ? `${tTitleBase} – ${kw}` : tTitleBase);
                      }
                    }}
                    style={{ resize: "vertical", minHeight: 64 }}
                  />
                </label>
                {trelloError   && <div className="v-strg-error">{trelloError}</div>}
                {trelloSuccess && <div className="v-strg-success">{trelloSuccess}</div>}
              </div>
              <div className="v-modal-actions">
                <button
                  ref={trelloBeimVerschiebenRef}
                  className="v-btn primary"
                  style={{ background: "#9c27b0", borderColor: "#9c27b0" }}
                  disabled={trelloSaving}
                  title="Card wird erst beim Verschieben erstellt"
                  onClick={() => {
                    setFileSettings((prev) => ({
                      ...prev,
                      [trelloPopupPath]: { ...prev[trelloPopupPath], trello_enabled: true, trello_mode: "beim_verschieben", trello_grund: trelloGrund, trello_titel: trelloTitel },
                    }));
                    setTrelloPopupOpen(false);
                  }}
                >
                  📋 Card beim Verschieben
                </button>
                <button
                  className="v-btn"
                  style={{ borderColor: "#9c27b0", color: "#9c27b0" }}
                  disabled={trelloSaving}
                  onClick={async () => {
                    const res = await trelloCreateCard(trelloPopupPath, "direkt", trelloGrund, null, fileSettings[trelloPopupPath]?.prefix, fileSettings[trelloPopupPath]?.annotation, trelloTitel, fileSettings[trelloPopupPath]?.einheit, fileSettings[trelloPopupPath]?.raum);
                    if (res?.card_id) {
                      const fname = trelloPopupPath.split("/").pop() || "";
                      try { await apiPost("/fotos/update-trello", { foto_name: fname, card_id: res.card_id, card_url: res.card_url || "" }); } catch {}
                    }
                    if (res) setTimeout(() => setTrelloPopupOpen(false), 1200);
                  }}
                >
                  {trelloSaving ? "Erstelle..." : "⚡ Direkt Card erstellen"}
                </button>
                <button className="v-btn ghost" disabled={trelloSaving} onClick={() => setTrelloPopupOpen(false)}>Abbrechen</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ════════════════════════════════════════
          GeoConfig Modal (neuen GPS-Eintrag anlegen)
          ════════════════════════════════════════ */}
      {geoConfigModal && (
        <div className="v-overlay" onClick={() => setGeoConfigModal(null)}>
          <div className="v-modal fp-geoconfg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="v-modal-header">
              <h3 style={{ color: "#4dd0e1" }}>
                <FaMapMarkerAlt style={{ marginRight: 8 }} />
                Neuen GPS-Standort anlegen
              </h3>
              <button className="v-dismiss" onClick={() => setGeoConfigModal(null)}>&#215;</button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                handleGeoConfigCreate({
                  dateiPath: geoConfigModal.dateiPath,
                  lat:       geoConfigModal.lat,
                  lon:       geoConfigModal.lon,
                  radius_m:  fd.get("radius_m"),
                  adresse:   fd.get("adresse"),
                  objekt:    fd.get("objekt"),
                  prefix:    fd.get("prefix"),
                  pfad:      fd.get("pfad"),
                  bemerkung: fd.get("bemerkung"),
                });
              }}
            >
              <div className="fp-geoconfg-grid">
                <label className="v-strg-field">
                  <span>GPS (Lat)</span>
                  <input readOnly value={geoConfigModal.lat?.toFixed(6) || ""} style={{ opacity: 0.6 }} />
                </label>
                <label className="v-strg-field">
                  <span>GPS (Lon)</span>
                  <input readOnly value={geoConfigModal.lon?.toFixed(6) || ""} style={{ opacity: 0.6 }} />
                </label>
                <label className="v-strg-field">
                  <span>Radius (m)</span>
                  <input name="radius_m" type="number" defaultValue={200} />
                </label>
                <label className="v-strg-field">
                  <span>Adresse</span>
                  <input name="adresse" defaultValue={geoConfigModal.adresse || ""} placeholder="Straße, PLZ Ort" />
                </label>
                <label className="v-strg-field">
                  <span>Objekt</span>
                  <input name="objekt" placeholder="z.B. 101, SIG" required />
                </label>
                <label className="v-strg-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Prefix</span>
                  <input name="prefix" placeholder="z.B. 101-Allg (leer = Objekt)" />
                </label>
                <label className="v-strg-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Dropbox-Zielpfad</span>
                  <input name="pfad" placeholder="/Holthey Immobilien/... (leer = automatisch)" style={{ fontFamily: "monospace", fontSize: 11 }} />
                </label>
                <label className="v-strg-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Bemerkung</span>
                  <input name="bemerkung" placeholder="optional" />
                </label>
              </div>
              <div className="v-modal-actions">
                <button type="submit" className="v-btn primary" style={{ background: "#00838f", borderColor: "#00838f" }}>
                  <FaCheck /> Eintrag speichern
                </button>
                <button type="button" className="v-btn ghost" onClick={() => setGeoConfigModal(null)}>Abbrechen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Batch-Bestätigungs-Modal ── */}
      {showBatch && (
        <div className="v-overlay" onClick={() => { if (!batchMoving) setShowBatch(false); }}>
          <div className="v-modal" onClick={(e) => e.stopPropagation()}>
            <div className="v-modal-header">
              <h3><FaLayerGroup /> Massenverschieben</h3>
              <button className="v-dismiss" onClick={() => setShowBatch(false)} disabled={batchMoving}>×</button>
            </div>
            <p className="v-modal-desc">
              {batchCandidates.length} Fotos verschieben{checkedPaths.size > 0 ? " (markierte Auswahl)" : " (grüne + gelbe)"}:
            </p>
            <div className="v-batch-stats">
              <span className="v-tag green">
                {batchCandidates.filter((d) => getSetting(d.path).confidence === "green").length} sicher
              </span>
              <span className="v-tag" style={{ background: "#f9a825", color: "#000" }}>
                {batchCandidates.filter((d) => getSetting(d.path).confidence === "yellow").length} unsicher
              </span>
            </div>
            <div className="v-batch-list">
              {batchCandidates.map((d) => {
                const s = getSetting(d.path);
                const prog = batchProgress[d.path];
                return (
                  <div key={d.path} className="v-batch-item">
                    <span style={{ width: 18, display: 'inline-block', textAlign: 'center', flexShrink: 0 }}>
                      {prog === 'ok' && <FaCheck style={{ color: '#4caf50' }} />}
                      {prog === 'fehler' && <FaTimes style={{ color: '#ef5350' }} />}
                      {prog === 'running' && <FaSync className="spin" style={{ color: '#90caf9', fontSize: '0.8em' }} />}
                    </span>
                    <span style={{ opacity: prog === 'ok' ? 0.5 : 1 }}>{d.name}</span>
                    <span className="dim">→ {s.prefix || "?"} / {s.ziel_ordner ? s.ziel_ordner.split("/").pop() : "?"}</span>
                  </div>
                );
              })}
            </div>
            <div className="v-modal-actions">
              <button className="v-btn primary" onClick={runBatch} disabled={batchMoving}>
                {batchMoving ? "Verschiebe..." : "Alle " + batchCandidates.length + " verschieben"}
              </button>
              <button className="v-btn ghost" onClick={() => setShowBatch(false)} disabled={batchMoving}>
                Abbrechen
              </button>
            </div>
            {batchResult && (
              <div className={"v-toast " + (batchResult.fehlgeschlagen > 0 ? "error" : batchResult.trelloFehler > 0 ? "warning" : "success")}>
                {batchResult.erfolgreich} ok{batchResult.fehlgeschlagen > 0 ? ", " + batchResult.fehlgeschlagen + " fehl" : ""}{batchResult.trelloFehler > 0 ? ", " + batchResult.trelloFehler + " Trello-Card fehlgeschlagen" : ""}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Duplikat-Modal ── */}
      {duplikatInfo && (
        <DuplikatModal info={duplikatInfo} apiBase={API_BASE} />
      )}

      {/* Welle 80: gemeinsamer Engine-Picker (Cache / GPS neu) */}
      {enginePickerFile && (
        <EnginePicker
          file={enginePickerFile}
          title="Foto neu analysieren"
          options={[
            { key: "cache", label: "Aus Cache", desc: "nur Zuordnung/Ziel neu berechnen", accent: "cache" },
            { key: "gps", label: "GPS neu", desc: "Download + EXIF + Geocoding (force)", accent: "v1" },
          ]}
          onChoose={(key) => { const f = enginePickerFile; analyseEinzel(f.path, key === "gps"); }}
          onClose={() => setEnginePickerFile(null)}
        />
      )}
      {/* Hintergrund-Verschiebe-Jobs: Fortschritt + Ergebnis pro Datei */}
      <VerschiebenJobIndicator jobs={jobs} onDismiss={dismissJob} vorlauf={jobVorlauf} />
    </div>
  );
}
